import React, { memo, useState, useMemo, useRef, useCallback } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  ShoppingBag,
  Sparkles,
  Zap,
  Plus,
  Trash2,
  Package,
  Tag,
  Search,
  CheckCircle2,
  XCircle,
  Edit3,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Layers,
  ArrowRight,
  ExternalLink,
  ShieldCheck,
  Check,
  AlertCircle,
  Image as ImageIcon,
  Film,
  FileText,
  Paperclip,
  Truck,
  ClipboardCheck,
  Upload,
  Loader2,
  X,
  Wand2,
  Eye,
  RotateCcw,
  History,
  Camera,
  Archive,
  RefreshCw,
  Clock,
  AlertTriangle,
  FolderPlus,
  Folder,
  FolderOpen,
  Edit2,
  Copy,
  Smartphone,
  MessageSquare,
  Send,
  Bell,
} from 'lucide-react';
import type { MediaVaultAsset } from './MediaVaultNode';

export interface ProductMediaItem {
  id: string;
  type: 'image' | 'video' | 'pdf' | 'document';
  url: string;
  name?: string;
  size?: string;
}

export interface TieredPriceTier {
  minQuantity: number;
  maxQuantity?: number;
  unitPrice: number;
  description?: string;
}

export interface CatalogProduct {
  id: string;
  name: string;
  category: string;
  categories?: string[];
  price: number;
  originalPrice?: number;
  minPrice?: number;
  maxPrice?: number;
  currency?: string;
  unitType?: string;
  isAvailable: boolean;
  description?: string;
  sku?: string;
  imageUrl?: string;
  media?: ProductMediaItem[];
  searchKeywords?: string[];
  stockQuantity?: number;
  weight?: string;
  dimensions?: string;
  attributes?: Record<string, any>;
  specifications?: Array<{ key: string; value: string }>;
  tags?: string[];
  tieredPricing?: TieredPriceTier[];
  competitorPrice?: number;
  competitiveAdvantage?: string;
}

export interface CatalogRecycleItem {
  id: string;
  product: CatalogProduct;
  deletedAt: string;
  deletedBy?: 'luna_ai' | 'user' | string;
}

export interface CatalogSnapshot {
  id: string;
  timestamp: string;
  label: string;
  productCount: number;
  products: CatalogProduct[];
  trigger: 'manual' | 'pre_delete' | 'restore' | string;
}

export interface CategoryRecycleItem {
  id: string;
  name: string;
  deletedAt: string;
  productCount: number;
}

export interface OrderTemplateFieldConfig {
  customerName?: boolean;
  deliveryAddress?: boolean;
  city?: boolean;
  primaryPhone?: boolean;
  secondaryPhone?: boolean;
  whatsappNumber?: boolean;
  landmark?: boolean;
  notes?: boolean;
  customFields?: Array<{ key: string; label: string; required?: boolean }>;
}

export interface OrderTemplateConfig {
  enabled?: boolean;
  fields?: OrderTemplateFieldConfig;
  ownerConfirmTemplate?: string;
  ownerCourierTemplate?: string;
  customerConfirmationTemplate?: string;
}

export const DEFAULT_OWNER_CONFIRM_TEMPLATE = `🛍️ *NEW ORDER CONFIRMED!*
━━━━━━━━━━━━━━━━━━━━━━━━━
🆔 *Order ID:* *#{{orderNumber}}*
🏢 *Store:* {{storeName}}
📅 *Type:* 📦 *{{orderType}}*
━━━━━━━━━━━━━━━━━━━━━━━━━
💬 *Customer Active WhatsApp:* *{{whatsappNumber}}*
👤 *Customer Name:* {{customerName}}
📞 *Primary Contact Phone:* {{customerPhone}}
📱 *Secondary Phone:* {{secondaryPhone}}
📍 *Delivery Address:* {{deliveryAddress}}
🏙️ *City / Town:* {{city}}
━━━━━━━━━━━━━━━━━━━━━━━━━
📦 *Ordered Items:*
{{itemsList}}
💵 *Total Amount / COD:* *{{currency}} {{totalAmount}}*
💳 *Payment Method:* {{paymentMethod}}
📝 *Customer Notes:* {{notes}}
━━━━━━━━━━━━━━━━━━━━━━━━━
✅ *Recorded in Tenet Live Dashboard:*
👉 https://kangavena.com/tenet (Orders & Live Tracking Studio)
⏰ *Time:* {{orderTime}}
⚡ *Autonomous Order Engine — Kangavena Luna AI*`;

export const DEFAULT_COURIER_TEMPLATE = `📦 *NEW COURIER DISPATCH ORDER*
━━━━━━━━━━━━━━━━━━━━━━━━━
🆔 *Order ID:* {{orderNumber}}
🏢 *Store:* {{storeName}}
━━━━━━━━━━━━━━━━━━━━━━━━━
👤 *Recipient Name:* {{customerName}}
📍 *Delivery Address:* {{deliveryAddress}}
🏙️ *City / Town:* {{city}}
📞 *Primary Phone:* {{customerPhone}}
📱 *Secondary Phone:* {{secondaryPhone}}
💬 *WhatsApp Number:* {{whatsappNumber}}
━━━━━━━━━━━━━━━━━━━━━━━━━
📦 *Ordered Items:*
{{itemsList}}
💵 *COD / Total Payable:* {{currency}} {{totalAmount}}
💳 *Payment Method:* {{paymentMethod}}
📝 *Delivery Notes / Landmark:* {{notes}}
━━━━━━━━━━━━━━━━━━━━━━━━━
⏰ *Time:* {{orderTime}}
⚡ *Kangavena Luna AI — 1-Tap Courier Dispatch*`;

export const DEFAULT_CUSTOMER_RECEIPT_TEMPLATE = `🛍️ *ORDER CONFIRMED — THANK YOU!*
━━━━━━━━━━━━━━━━━━━━━━━━━
Hi {{customerName}}, your order has been successfully placed with *{{storeName}}*! 🎉

🆔 *Order ID:* {{orderNumber}}
📍 *Delivery To:* {{deliveryAddress}}, {{city}}
📞 *Contact Phone:* {{customerPhone}}
📱 *Backup Phone:* {{secondaryPhone}}
💬 *WhatsApp Number:* {{whatsappNumber}}
📦 *Order Summary:*
{{itemsList}}
💵 *Total Payable:* {{currency}} {{totalAmount}}
💳 *Payment Method:* {{paymentMethod}}

🚚 Our team is preparing your package for courier dispatch. You will receive live tracking updates once the courier partner collects your parcel!`;

export interface PreFilledMessageTemplate {
  id: string;
  title: string;
  triggerType: 'first_contact_greeting' | 'keyword_match' | 'product_inquiry';
  triggerKeywords: string[];
  messageText: string;
  attachedProductId?: string;
  attachedProductIds?: string[];
  attachedMediaUrl?: string;
  attachedMediaUrls?: string[];
  attachedMediaType?: 'image' | 'video' | 'pdf' | 'document';
  attachedMediaVaultAssetIds?: string[];
  selectedProductImages?: Record<string, string[]>;
  sendProductsIndividually?: boolean;
  packMediaAlbum?: boolean;
  isEnabled: boolean;
  priority?: number;
}

export const DEFAULT_PRE_FILLED_TEMPLATES: PreFilledMessageTemplate[] = [
  {
    id: 'tmpl_welcome_greeting',
    title: '👋 Welcome Greeting & Quick Assistance',
    triggerType: 'first_contact_greeting',
    triggerKeywords: ['hi', 'hello', 'hey', 'start', 'wisthara', 'details', 'help', 'info', 'ආයුබෝවන්', 'හලෝ'],
    messageText: `👋 Hello {{customer_name}}! Welcome to *{{store_name}}*.\n\nHow can we help you today? Feel free to ask about any of our products, check prices, or place an order directly here! 🛍️✨`,
    packMediaAlbum: true,
    isEnabled: true,
    priority: 10,
  }
];

export interface ProductCatalogNodeData {
  products?: CatalogProduct[];
  categories?: string[];
  categoryRules?: Record<string, string>;
  categoryRecycleBin?: CategoryRecycleItem[];
  recycleBin?: CatalogRecycleItem[];
  snapshots?: CatalogSnapshot[];
  defaultCurrency?: string;
  allowAiDiscounts?: boolean;
  enableOrderTaking?: boolean;
  enableOrderTracking?: boolean;
  orderNotificationPhone?: string;
  orderTemplateConfig?: OrderTemplateConfig;
  preFilledTemplatesEnabled?: boolean;
  preFilledTemplates?: PreFilledMessageTemplate[];
  [key: string]: unknown;
}

export const DEFAULT_SAMPLE_PRODUCTS: CatalogProduct[] = [
  {
    id: 'prod_red_rose',
    name: '🌹 Red Rose Romance Bouquet',
    category: 'Bouquets',
    price: 4500,
    originalPrice: 5500,
    minPrice: 4000,
    maxPrice: 4500,
    currency: 'LKR',
    unitType: 'Pieces',
    isAvailable: true,
    description: 'Fresh 24 imported red roses with premium velvet wrapping & greeting card.',
    sku: 'FLW-ROSE-01',
    imageUrl: 'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=600&auto=format&fit=crop&q=80',
    media: [
      {
        id: 'med_rose_img1',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=600&auto=format&fit=crop&q=80',
        name: 'Red Roses High Res Photo'
      },
      {
        id: 'med_rose_pdf1',
        type: 'pdf',
        url: '/uploads/luna-assets/rose-care-guide.pdf',
        name: 'Flower Care & Longevity Guide (PDF)'
      }
    ],
    tags: ['Romance', 'Anniversary', 'Valentine', 'Premium'],
    searchKeywords: ['red rose', 'rose bouquet', 'rathu mal', 'valentine flowers', 'romantic gift']
  },
  {
    id: 'prod_white_lily',
    name: '🌸 White Lily & Jasmine Bunch',
    category: 'Bouquets',
    price: 3800,
    originalPrice: 4200,
    minPrice: 3500,
    maxPrice: 3800,
    currency: 'LKR',
    unitType: 'Pieces',
    isAvailable: true,
    description: 'Fragrant white lilies and royal jasmine in artisan rustic paper wrapping.',
    sku: 'FLW-LILY-02',
    imageUrl: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=600&auto=format&fit=crop&q=80',
    media: [
      {
        id: 'med_lily_img1',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=600&auto=format&fit=crop&q=80',
        name: 'White Lily High-Res'
      }
    ],
    tags: ['Lilies', 'Jasmine', 'Fresh Fragrance'],
    searchKeywords: ['white lily', 'jasmine bunch', 'sudu mal', 'fragrant flowers']
  },
  {
    id: 'prod_wedding_pkg',
    name: '💎 VIP Wedding Stage Floral Package',
    category: 'Weddings',
    price: 75000,
    originalPrice: 85000,
    minPrice: 70000,
    maxPrice: 75000,
    currency: 'LKR',
    unitType: 'Set',
    isAvailable: true,
    description: 'Complete luxury wedding stage, poruwa, and table centerpiece flower decorations.',
    sku: 'FLW-WED-VIP',
    imageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&auto=format&fit=crop&q=80',
    media: [
      {
        id: 'med_wed_img1',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&auto=format&fit=crop&q=80',
        name: 'Stage Decor HD Photo'
      },
      {
        id: 'med_wed_pdf1',
        type: 'pdf',
        url: '/uploads/luna-assets/vip-wedding-catalog-2026.pdf',
        name: 'Wedding Package Brochure & Color Themes (PDF)'
      }
    ],
    tags: ['Wedding', 'Poruwa', 'Luxury Stage', 'Decor'],
    searchKeywords: ['wedding package', 'flower stage', 'poruwa decor', 'magul mal set', 'vip decor']
  },
];

export const ProductCatalogNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();
  const nodeData = (data || {}) as ProductCatalogNodeData;

  const rawProducts = Array.isArray(nodeData.products)
    ? nodeData.products
    : (Array.isArray((nodeData.widgetData as any)?.products)
      ? (nodeData.widgetData as any).products
      : (nodeData.products === undefined && !nodeData.widgetData ? DEFAULT_SAMPLE_PRODUCTS : []));

  const products: CatalogProduct[] = rawProducts;

  const rawRecycleBin: CatalogRecycleItem[] = Array.isArray(nodeData.recycleBin)
    ? nodeData.recycleBin
    : (Array.isArray((nodeData.widgetData as any)?.recycleBin)
      ? (nodeData.widgetData as any).recycleBin
      : []);

  const rawSnapshots: CatalogSnapshot[] = Array.isArray(nodeData.snapshots)
    ? nodeData.snapshots
    : (Array.isArray((nodeData.widgetData as any)?.snapshots)
      ? (nodeData.widgetData as any).snapshots
      : []);

  const defaultCurrency = nodeData.defaultCurrency || 'LKR';
  const allowAiDiscounts = nodeData.allowAiDiscounts === true;

  const [enableOrderTaking, setEnableOrderTaking] = useState<boolean>(nodeData.enableOrderTaking !== false);
  const [enableOrderTracking, setEnableOrderTracking] = useState<boolean>(nodeData.enableOrderTracking !== false);
  const [orderNotificationPhone, setOrderNotificationPhone] = useState<string>((nodeData.orderNotificationPhone as string) || '');
  const [showOrderSettings, setShowOrderSettings] = useState(false);

  const rawOrderTemplateConfig = (nodeData.orderTemplateConfig && typeof nodeData.orderTemplateConfig === 'object')
    ? nodeData.orderTemplateConfig
    : (((nodeData.widgetData as any)?.orderTemplateConfig && typeof (nodeData.widgetData as any).orderTemplateConfig === 'object')
      ? (nodeData.widgetData as any).orderTemplateConfig
      : {});

  const [orderTemplateConfig, setOrderTemplateConfig] = useState<OrderTemplateConfig>({
    enabled: rawOrderTemplateConfig.enabled !== false,
    fields: {
      customerName: rawOrderTemplateConfig.fields?.customerName !== false,
      deliveryAddress: rawOrderTemplateConfig.fields?.deliveryAddress !== false,
      city: rawOrderTemplateConfig.fields?.city !== false,
      primaryPhone: rawOrderTemplateConfig.fields?.primaryPhone !== false,
      secondaryPhone: rawOrderTemplateConfig.fields?.secondaryPhone !== false,
      whatsappNumber: rawOrderTemplateConfig.fields?.whatsappNumber !== false,
      landmark: rawOrderTemplateConfig.fields?.landmark === true,
      notes: rawOrderTemplateConfig.fields?.notes !== false,
      customFields: Array.isArray(rawOrderTemplateConfig.fields?.customFields) ? rawOrderTemplateConfig.fields.customFields : [],
    },
    ownerConfirmTemplate: (rawOrderTemplateConfig.ownerConfirmTemplate !== undefined && rawOrderTemplateConfig.ownerConfirmTemplate !== null)
      ? rawOrderTemplateConfig.ownerConfirmTemplate
      : DEFAULT_OWNER_CONFIRM_TEMPLATE,
    ownerCourierTemplate: (rawOrderTemplateConfig.ownerCourierTemplate !== undefined && rawOrderTemplateConfig.ownerCourierTemplate !== null)
      ? rawOrderTemplateConfig.ownerCourierTemplate
      : DEFAULT_COURIER_TEMPLATE,
    customerConfirmationTemplate: (rawOrderTemplateConfig.customerConfirmationTemplate !== undefined && rawOrderTemplateConfig.customerConfirmationTemplate !== null)
      ? rawOrderTemplateConfig.customerConfirmationTemplate
      : DEFAULT_CUSTOMER_RECEIPT_TEMPLATE,
  });

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateActiveTab, setTemplateActiveTab] = useState<'fields' | 'owner_alert' | 'courier' | 'customer' | 'preview'>('fields');
  const [newCustomFieldKey, setNewCustomFieldKey] = useState('');
  const [newCustomFieldLabel, setNewCustomFieldLabel] = useState('');
  const [newCustomFieldRequired, setNewCustomFieldRequired] = useState(false);
  const [copiedPreview, setCopiedPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState<'owner_alert' | 'courier' | 'customer'>('owner_alert');

  const rawPreFilledEnabled = (() => {
    const raw = (nodeData.preFilledTemplatesEnabled !== undefined)
      ? nodeData.preFilledTemplatesEnabled
      : (nodeData.widgetData as any)?.preFilledTemplatesEnabled;
    return raw !== false && raw !== 'false' && raw !== 0 && raw !== '0';
  })();

  const rawPreFilledTemplates = (Array.isArray(nodeData.preFilledTemplates)
    ? (nodeData.preFilledTemplates as PreFilledMessageTemplate[])
    : (Array.isArray((nodeData.widgetData as any)?.preFilledTemplates)
      ? ((nodeData.widgetData as any).preFilledTemplates as PreFilledMessageTemplate[])
      : DEFAULT_PRE_FILLED_TEMPLATES)
  ).map((t: any) => ({
    ...t,
    packMediaAlbum: t.packMediaAlbum !== false,
    isEnabled: t.isEnabled !== false && t.isEnabled !== 'false' && t.isEnabled !== 0 && t.isEnabled !== '0' && t.isEnabled !== 'disabled' && t.isEnabled !== 'paused',
  }));

  const [preFilledTemplatesEnabled, setPreFilledTemplatesEnabled] = useState<boolean>(rawPreFilledEnabled);
  const [preFilledTemplates, setPreFilledTemplates] = useState<PreFilledMessageTemplate[]>(rawPreFilledTemplates);
  const [showPreFilledDrawer, setShowPreFilledDrawer] = useState<boolean>(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState<Partial<PreFilledMessageTemplate>>({});
  const [keywordInputDraft, setKeywordInputDraft] = useState<string>('');
  const [copiedPreFilledPreview, setCopiedPreFilledPreview] = useState<boolean>(false);
  const [showMediaVaultBrowser, setShowMediaVaultBrowser] = useState<boolean>(false);
  const [mediaVaultSearch, setMediaVaultSearch] = useState<string>('');
  const [mediaVaultFilterType, setMediaVaultFilterType] = useState<'all' | 'image' | 'video' | 'pdf'>('all');

  const availableMediaVaultAssets: MediaVaultAsset[] = useMemo(() => {
    try {
      const nodes = getNodes();
      const mvNode = nodes.find((n) => n.type === 'MediaVaultNode');
      if (!mvNode?.data) return [];
      const raw = (mvNode.data.assets as any) || ((mvNode.data.widgetData as any)?.assets as any) || [];
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  }, [getNodes]);

  const rawCategoryRules: Record<string, string> = (nodeData.categoryRules && typeof nodeData.categoryRules === 'object')
    ? nodeData.categoryRules
    : (((nodeData.widgetData as any)?.categoryRules && typeof (nodeData.widgetData as any).categoryRules === 'object')
      ? (nodeData.widgetData as any).categoryRules
      : {});
  const [categoryRules, setCategoryRules] = useState<Record<string, string>>(rawCategoryRules);
  const [newCategoryRuleInput, setNewCategoryRuleInput] = useState<string>('');
  const [editingCategoryRule, setEditingCategoryRule] = useState<{ catName: string; rule: string } | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [renamingCategory, setRenamingCategory] = useState<{ oldName: string; newName: string } | null>(null);
  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('flat');
  const [categoryTab, setCategoryTab] = useState<'active' | 'recycle'>('active');
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [manualSnapshotLabel, setManualSnapshotLabel] = useState('');
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // New Product State
  const [newProdName, setNewProdName] = useState('');
  const [newProdCategory, setNewProdCategory] = useState('General');
  const [newProdCategories, setNewProdCategories] = useState<string[]>(['General']);
  const [newProdCategoryTagInput, setNewProdCategoryTagInput] = useState('');
  const [newProdPrice, setNewProdPrice] = useState<string>('');
  const [newProdOriginalPrice, setNewProdOriginalPrice] = useState<string>('');
  const [newProdStockQty, setNewProdStockQty] = useState<string>('50');
  const [newProdUnit, setNewProdUnit] = useState('Pieces');
  const [newProdDesc, setNewProdDesc] = useState('');
  const [newProdRawText, setNewProdRawText] = useState('');
  const [newProdMediaUrl, setNewProdMediaUrl] = useState('');
  const [newProdKeywords, setNewProdKeywords] = useState('');
  const [newProdAvailable, setNewProdAvailable] = useState(true);

  // Edit Product State
  const [editingProd, setEditingProd] = useState<CatalogProduct | null>(null);
  const [editProdName, setEditProdName] = useState('');
  const [editProdCategory, setEditProdCategory] = useState('General');
  const [editProdCategories, setEditProdCategories] = useState<string[]>(['General']);
  const [editProdCategoryTagInput, setEditProdCategoryTagInput] = useState('');
  const [quickCategoryProdId, setQuickCategoryProdId] = useState<string | null>(null);
  const [quickNewCategoryInput, setQuickNewCategoryInput] = useState('');
  const [editProdPrice, setEditProdPrice] = useState<string>('');
  const [editProdOriginalPrice, setEditProdOriginalPrice] = useState<string>('');
  const [editProdStockQty, setEditProdStockQty] = useState<string>('50');
  const [editProdUnit, setEditProdUnit] = useState('Pieces');
  const [editProdDesc, setEditProdDesc] = useState('');
  const [editProdRawText, setEditProdRawText] = useState('');
  const [editProdMediaUrl, setEditProdMediaUrl] = useState('');
  const [editProdKeywords, setEditProdKeywords] = useState('');
  const [editProdAvailable, setEditProdAvailable] = useState(true);

  // Multi-Media & Luna AI State
  const [newProdMedia, setNewProdMedia] = useState<ProductMediaItem[]>([]);
  const [editProdMedia, setEditProdMedia] = useState<ProductMediaItem[]>([]);
  const [isAiEnriching, setIsAiEnriching] = useState<boolean>(false);
  const [isEditAiEnriching, setIsEditAiEnriching] = useState<boolean>(false);
  const [selectedPreviewMedia, setSelectedPreviewMedia] = useState<{ title: string; url: string; type: string } | null>(null);

  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isEditUploadingMedia, setIsEditUploadingMedia] = useState(false);
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const getAuthHeaders = () => {
    const headers: Record<string, string> = {};
    if (isTenantView) {
      if (authToken) headers['Authorization'] = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (!headers['Authorization'] && tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
    } else {
      const token = sessionStorage.getItem('adminToken') || '';
      const godToken = sessionStorage.getItem('godModeToken') || '';
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      else if (tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
      if (godToken) headers['X-God-Mode-Token'] = godToken;
    }
    return headers;
  };

  const handleSingleFileUpload = async (file: File, isEdit: boolean = false) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.success && data.url) {
        const fullUrl = data.url.startsWith('http')
          ? data.url
          : `${window.location.origin}${data.url}`;

        let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
        if (file.type?.startsWith('video/')) mType = 'video';
        else if (file.type === 'application/pdf') mType = 'pdf';
        else if (file.type?.includes('document') || file.type?.includes('sheet') || file.type?.includes('text')) mType = 'document';

        const newItem: ProductMediaItem = {
          id: `med_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          type: mType,
          url: fullUrl,
          name: file.name || 'Attached Media',
          size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
        };

        if (isEdit) {
          setEditProdMedia(prev => [...prev, newItem]);
          if (!editProdMediaUrl && mType === 'image') setEditProdMediaUrl(fullUrl);
        } else {
          setNewProdMedia(prev => [...prev, newItem]);
          if (!newProdMediaUrl && mType === 'image') setNewProdMediaUrl(fullUrl);
        }
      }
    } catch (err: any) {
      console.error('[ProductCatalogNode] Single upload error:', err);
    }
  };

  const handleMultiFileUpload = async (files: FileList | File[], isEdit: boolean = false) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    if (isEdit) setIsEditUploadingMedia(true);
    else setIsUploadingMedia(true);

    try {
      const formData = new FormData();
      for (const f of fileArray) {
        formData.append('files', f);
      }

      const res = await fetch('/api/admin/luna-flows/upload-assets-batch', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.success && Array.isArray(data.assets) && data.assets.length > 0) {
        const newItems: ProductMediaItem[] = data.assets.map((a: any, idx: number) => {
          const fullUrl = a.url.startsWith('http') ? a.url : `${window.location.origin}${a.url}`;
          let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
          if (a.mimeType?.startsWith('video/') || a.mediaType === 'video') mType = 'video';
          else if (a.mimeType === 'application/pdf' || a.mediaType === 'pdf') mType = 'pdf';
          else if (a.mimeType?.includes('document') || a.mimeType?.includes('sheet') || a.mimeType?.includes('text')) mType = 'document';

          return {
            id: `med_${Date.now()}_${idx}`,
            type: mType,
            url: fullUrl,
            name: a.originalName || a.filename || 'Attached Asset',
            size: a.size ? `${(a.size / (1024 * 1024)).toFixed(2)} MB` : undefined,
          };
        });

        if (isEdit) {
          setEditProdMedia(prev => [...prev, ...newItems]);
          const firstImg = newItems.find(m => m.type === 'image');
          if (!editProdMediaUrl && firstImg) setEditProdMediaUrl(firstImg.url);
        } else {
          setNewProdMedia(prev => [...prev, ...newItems]);
          const firstImg = newItems.find(m => m.type === 'image');
          if (!newProdMediaUrl && firstImg) setNewProdMediaUrl(firstImg.url);
        }
      } else {
        // Fallback to single upload loop
        for (const file of fileArray) {
          await handleSingleFileUpload(file, isEdit);
        }
      }
    } catch (err: any) {
      console.error('[ProductCatalogNode] Multi-upload error:', err);
      for (const file of fileArray) {
        await handleSingleFileUpload(file, isEdit);
      }
    } finally {
      if (isEdit) setIsEditUploadingMedia(false);
      else setIsUploadingMedia(false);
    }
  };

  const handleAiImproveProduct = async (isEdit: boolean = false) => {
    const targetMedia = isEdit ? editProdMedia : newProdMedia;
    const targetUrl = isEdit ? editProdMediaUrl : newProdMediaUrl;
    const currentName = isEdit ? editProdName : newProdName;
    const currentCat = isEdit ? editProdCategory : newProdCategory;
    const currentPrice = isEdit ? editProdPrice : newProdPrice;
    const currentOrig = isEdit ? editProdOriginalPrice : newProdOriginalPrice;
    const currentStock = isEdit ? editProdStockQty : newProdStockQty;
    const currentUnit = isEdit ? editProdUnit : newProdUnit;
    const currentDesc = isEdit ? editProdDesc : newProdDesc;
    const currentKeywords = isEdit ? editProdKeywords : newProdKeywords;
    const targetRawText = (isEdit ? editProdRawText : newProdRawText).trim();

    if (targetMedia.length === 0 && !targetUrl.trim() && !currentName.trim() && !currentDesc.trim() && !targetRawText) {
      alert('Please upload product images, enter draft details, or paste ChatGPT / supplier specs for Luna AI to analyze!');
      return;
    }

    if (isEdit) setIsEditAiEnriching(true);
    else setIsAiEnriching(true);

    try {
      const endpoint = isTenantView
        ? '/api/tenet/products/ai-improve-product'
        : '/api/admin/luna-flows/ai-improve-product';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: currentName,
          category: currentCat,
          price: currentPrice,
          originalPrice: currentOrig,
          stockQuantity: currentStock,
          unitType: currentUnit,
          description: currentDesc,
          searchKeywords: currentKeywords,
          media: targetMedia,
          imageUrl: targetUrl,
          rawProductText: targetRawText,
          currency: defaultCurrency,
        }),
      });

      const data = await res.json();
      if (data.success && data.improvedProduct) {
        const p = data.improvedProduct;
        if (isEdit) {
          if (p.name) setEditProdName(p.name);
          if (p.category) setEditProdCategory(p.category);
          if (p.price && (!editProdPrice || editProdPrice === '0')) setEditProdPrice(String(p.price));
          if (p.originalPrice) setEditProdOriginalPrice(String(p.originalPrice));
          if (p.stockQuantity !== undefined && p.stockQuantity !== null) setEditProdStockQty(String(p.stockQuantity));
          if (p.unitType) setEditProdUnit(p.unitType);
          if (p.description) setEditProdDesc(p.description);
          if (Array.isArray(p.searchKeywords) && p.searchKeywords.length > 0) {
            setEditProdKeywords(p.searchKeywords.join(', '));
          }
        } else {
          if (p.name) setNewProdName(p.name);
          if (p.category) setNewProdCategory(p.category);
          if (p.price && (!newProdPrice || newProdPrice === '0')) setNewProdPrice(String(p.price));
          if (p.originalPrice) setNewProdOriginalPrice(String(p.originalPrice));
          if (p.stockQuantity !== undefined && p.stockQuantity !== null) setNewProdStockQty(String(p.stockQuantity));
          if (p.unitType) setNewProdUnit(p.unitType);
          if (p.description) setNewProdDesc(p.description);
          if (Array.isArray(p.searchKeywords) && p.searchKeywords.length > 0) {
            setNewProdKeywords(p.searchKeywords.join(', '));
          }
        }
        setSaveIndicator('✨ Luna AI Enriched!');
        setTimeout(() => setSaveIndicator(null), 4000);
      } else {
        const rawErr = String(data.error || 'Luna AI could not improve product details.');
        const cleanMsg = rawErr
          .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
          .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
          .replace(/GoogleGenAI/gi, 'Luna AI')
          .replace(/Gemini AI models/gi, 'Luna AI')
          .replace(/Gemini AI/gi, 'Luna AI')
          .replace(/Gemini/gi, 'Luna AI');
        alert(cleanMsg);
      }
    } catch (err: any) {
      console.error('[ProductCatalogNode] AI Improve error:', err);
      const rawMsg = String(err?.message || 'Network error');
      const cleanMsg = rawMsg
        .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/GoogleGenAI/gi, 'Luna AI')
        .replace(/Gemini/gi, 'Luna AI');
      alert('Luna AI Notice: ' + cleanMsg);
    } finally {
      if (isEdit) setIsEditAiEnriching(false);
      else setIsAiEnriching(false);
    }
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const explicitCategories: string[] = Array.isArray(nodeData.categories) ? nodeData.categories : [];
  const rawCategoryRecycle: CategoryRecycleItem[] = Array.isArray(nodeData.categoryRecycleBin) ? nodeData.categoryRecycleBin : [];

  const getProductCategories = useCallback((p: any): string[] => {
    const set = new Set<string>();
    if (Array.isArray(p?.categories)) {
      p.categories.forEach((c: any) => {
        if (typeof c === 'string' && c.trim()) set.add(c.trim());
      });
    }
    if (typeof p?.category === 'string' && p.category.trim()) {
      p.category.split(',').forEach((c: string) => {
        if (c.trim()) set.add(c.trim());
      });
    }
    return set.size > 0 ? Array.from(set) : ['General'];
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    explicitCategories.forEach(c => { if (c && typeof c === 'string' && c.trim()) set.add(c.trim()); });
    products.forEach(p => {
      getProductCategories(p).forEach(c => set.add(c));
    });
    if (set.size === 0) set.add('General');
    return Array.from(set);
  }, [explicitCategories, products, getProductCategories]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = !searchQuery.trim() || 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (Array.isArray(p.searchKeywords) && p.searchKeywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())));
      const matchesCat = selectedCategory === 'all' || getProductCategories(p).some(c => c.toLowerCase() === selectedCategory.trim().toLowerCase());
      return matchesSearch && matchesCat;
    });
  }, [products, searchQuery, selectedCategory, getProductCategories]);

  const persistNodeDataDirectly = (allUpdates: Partial<ProductCatalogNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          products,
          categories: explicitCategories,
          categoryRules,
          categoryRecycleBin: rawCategoryRecycle,
          defaultCurrency,
          allowAiDiscounts,
          enableOrderTaking,
          enableOrderTracking,
          orderNotificationPhone,
          orderTemplateConfig,
          preFilledTemplatesEnabled,
          preFilledTemplates,
          ...allUpdates,
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            flowId: isTenantView ? undefined : (window as any).__CURRENT_FLOW_ID__,
            nodeId: id,
            widgetType: 'PRODUCT_CATALOG',
            data: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to Flow');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[ProductCatalogNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleUpdateProducts = (
    updatedProducts: CatalogProduct[],
    updatedCategories?: string[],
    updatedCategoryRecycle?: CategoryRecycleItem[],
    updatedCategoryRules?: Record<string, string>
  ) => {
    const finalCats = updatedCategories || explicitCategories;
    const finalCategoryRecycle = updatedCategoryRecycle || rawCategoryRecycle;
    const finalRules = updatedCategoryRules !== undefined ? updatedCategoryRules : categoryRules;

    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, products: updatedProducts, categories: finalCats, categoryRecycleBin: finalCategoryRecycle, categoryRules: finalRules } } : n))
    );
    if (updatedCategoryRules !== undefined) {
      setCategoryRules(finalRules);
    }
    persistNodeDataDirectly({ products: updatedProducts, categories: finalCats, categoryRecycleBin: finalCategoryRecycle, categoryRules: finalRules });
  };

  const handleAddCategory = (catName: string, ruleText?: string) => {
    const trimmed = catName.trim();
    if (!trimmed) return;
    const existing = categories.find(c => c.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setSelectedCategory(existing);
      setNewCategoryInput('');
      setNewCategoryRuleInput('');
      setShowCategoryManager(false);
      return;
    }
    const updated = [...explicitCategories, trimmed];
    const updatedRules = { ...categoryRules };
    if (ruleText && ruleText.trim()) {
      updatedRules[trimmed] = ruleText.trim();
      setCategoryRules(updatedRules);
    }
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, categories: updated, categoryRules: updatedRules } } : n))
    );
    persistNodeDataDirectly({ categories: updated, categoryRules: updatedRules });
    setSelectedCategory(trimmed);
    setNewCategoryInput('');
    setNewCategoryRuleInput('');
    setSaveIndicator(`✅ Created category "${trimmed}"`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleRenameCategory = (oldName: string, newName: string) => {
    const trimmedOld = oldName.trim();
    const trimmedNew = newName.trim();
    if (!trimmedNew || trimmedOld.toLowerCase() === trimmedNew.toLowerCase()) {
      setRenamingCategory(null);
      return;
    }
    const updatedCats = explicitCategories.map(c => c.toLowerCase() === trimmedOld.toLowerCase() ? trimmedNew : c);
    if (!updatedCats.some(c => c.toLowerCase() === trimmedNew.toLowerCase())) {
      updatedCats.push(trimmedNew);
    }
    const updatedProducts = products.map(p => {
      const pCats = getProductCategories(p);
      if (pCats.some(c => c.toLowerCase() === trimmedOld.toLowerCase())) {
        const newCats = pCats.map(c => c.toLowerCase() === trimmedOld.toLowerCase() ? trimmedNew : c);
        return {
          ...p,
          category: (p.category || 'General').trim().toLowerCase() === trimmedOld.toLowerCase() ? trimmedNew : (newCats[0] || 'General'),
          categories: newCats
        };
      }
      return p;
    });

    const updatedRules = { ...categoryRules };
    if (updatedRules[trimmedOld] !== undefined) {
      updatedRules[trimmedNew] = updatedRules[trimmedOld];
      delete updatedRules[trimmedOld];
      setCategoryRules(updatedRules);
    }

    handleUpdateProducts(updatedProducts, updatedCats, undefined, updatedRules);
    if (selectedCategory.toLowerCase() === trimmedOld.toLowerCase()) {
      setSelectedCategory(trimmedNew);
    }
    setRenamingCategory(null);
    setSaveIndicator(`✅ Renamed to "${trimmedNew}"`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleSaveCategoryRule = (catName: string, rule: string) => {
    const updatedRules = { ...categoryRules };
    if (rule.trim() === '') {
      delete updatedRules[catName];
    } else {
      updatedRules[catName] = rule.trim();
    }
    setCategoryRules(updatedRules);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, categoryRules: updatedRules } } : n))
    );
    persistNodeDataDirectly({ categoryRules: updatedRules });
    setEditingCategoryRule(null);
    setSaveIndicator(`✅ Updated rules for "${catName}"`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleDeleteCategory = (catToDelete: string) => {
    const trimmed = catToDelete.trim();
    if (trimmed.toLowerCase() === 'general') return;
    const confirmDelete = window.confirm(`Delete category "${trimmed}"? Existing products in this category will be moved to "General". You can restore this category anytime from the Category Recycle Bin.`);
    if (!confirmDelete) return;

    const updatedCats = explicitCategories.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
    const affectedCount = products.filter(p => getProductCategories(p).some(c => c.toLowerCase() === trimmed.toLowerCase())).length;
    const newRecycleEntry: CategoryRecycleItem = {
      id: `cat_del_${Date.now()}`,
      name: trimmed,
      deletedAt: new Date().toISOString(),
      productCount: affectedCount,
    };
    const updatedCategoryRecycle = [newRecycleEntry, ...rawCategoryRecycle];

    const updatedProducts = products.map(p => {
      const pCats = getProductCategories(p);
      if (pCats.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
        const remainingCats = pCats.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
        const finalCats = remainingCats.length > 0 ? remainingCats : ['General'];
        return {
          ...p,
          category: (p.category || 'General').trim().toLowerCase() === trimmed.toLowerCase() ? finalCats[0] : (finalCats[0] || 'General'),
          categories: finalCats
        };
      }
      return p;
    });

    handleUpdateProducts(updatedProducts, updatedCats, updatedCategoryRecycle);
    if (selectedCategory.toLowerCase() === trimmed.toLowerCase()) {
      setSelectedCategory('all');
    }
    setSaveIndicator(`🗑️ Moved category "${trimmed}" to Recycle Bin`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleRestoreCategory = (catRecycleId: string) => {
    const entry = rawCategoryRecycle.find(e => e.id === catRecycleId);
    if (!entry) return;
    const catName = entry.name;
    const updatedCategoryRecycle = rawCategoryRecycle.filter(e => e.id !== catRecycleId);
    const updatedCats = [...explicitCategories];
    if (!updatedCats.some(c => c.toLowerCase() === catName.toLowerCase())) {
      updatedCats.push(catName);
    }
    handleUpdateProducts(products, updatedCats, updatedCategoryRecycle);
    setSelectedCategory(catName);
    setSaveIndicator(`✅ Restored category "${catName}"`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleToggleProductCategory = (prodId: string, categoryName: string) => {
    const trimmed = categoryName.trim();
    if (!trimmed) return;
    const updated = products.map(p => {
      if (p.id === prodId) {
        const current = getProductCategories(p);
        let nextCats: string[];
        if (current.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
          nextCats = current.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
          if (nextCats.length === 0) nextCats = ['General'];
        } else {
          nextCats = [...current.filter(c => c !== 'General'), trimmed];
        }
        return {
          ...p,
          categories: nextCats,
          category: nextCats[0] || 'General',
        };
      }
      return p;
    });
    handleUpdateProducts(updated);
    setSaveIndicator(`Updated category for item`);
    setTimeout(() => setSaveIndicator(null), 2500);
  };

  const handleMoveProductToCategoryOnly = (prodId: string, targetCategory: string) => {
    const trimmed = targetCategory.trim();
    if (!trimmed) return;
    const updated = products.map(p => {
      if (p.id === prodId) {
        return {
          ...p,
          categories: [trimmed],
          category: trimmed,
        };
      }
      return p;
    });
    handleUpdateProducts(updated);
    setSaveIndicator(`Moved to "${trimmed}"`);
    setTimeout(() => setSaveIndicator(null), 2500);
  };

  const handleQuickAddAndAssignCategory = (prodId: string, newCat: string) => {
    const trimmed = newCat.trim();
    if (!trimmed) return;
    let updatedExplicitCats = [...explicitCategories];
    if (!categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
      updatedExplicitCats.push(trimmed);
    }
    const updated = products.map(p => {
      if (p.id === prodId) {
        const current = getProductCategories(p);
        const nextCats = Array.from(new Set([...current.filter(c => c !== 'General'), trimmed]));
        return {
          ...p,
          categories: nextCats,
          category: nextCats[0] || 'General',
        };
      }
      return p;
    });
    handleUpdateProducts(updated, updatedExplicitCats);
    setQuickNewCategoryInput('');
    setSaveIndicator(`Created "${trimmed}" and assigned to item`);
    setTimeout(() => setSaveIndicator(null), 2500);
  };

  const handleToggleStock = (prodId: string) => {
    const updated = products.map(p => {
      if (p.id === prodId) {
        const nextAvail = !p.isAvailable;
        return {
          ...p,
          isAvailable: nextAvail,
          stockQuantity: nextAvail ? (p.stockQuantity && p.stockQuantity > 0 ? p.stockQuantity : 50) : 0
        };
      }
      return p;
    });
    handleUpdateProducts(updated);
  };

  const handleDeleteProduct = (prodId: string) => {
    const prodToDelete = products.find(p => p.id === prodId);
    if (!prodToDelete) return;

    const updated = products.filter(p => p.id !== prodId);
    
    // Auto Snapshot before deletion
    const autoSnapshot: CatalogSnapshot = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: `Pre-Delete: ${prodToDelete.name}`,
      productCount: products.length,
      products: JSON.parse(JSON.stringify(products)),
      trigger: 'pre_delete',
    };
    const nextSnapshots = [autoSnapshot, ...rawSnapshots].slice(0, 25);

    // Add to recycle bin
    const recycleItem: CatalogRecycleItem = {
      id: `rec_${Date.now()}_${prodToDelete.id}`,
      product: prodToDelete,
      deletedAt: new Date().toISOString(),
      deletedBy: 'user',
    };
    const nextRecycleBin = [recycleItem, ...rawRecycleBin].slice(0, 50);

    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          products: updated,
          recycleBin: nextRecycleBin,
          snapshots: nextSnapshots,
        }
      } : n))
    );

    persistNodeDataDirectly({
      products: updated,
      recycleBin: nextRecycleBin,
      snapshots: nextSnapshots,
    });
    setSaveIndicator(`Moved to Recycle Bin`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleRestoreFromRecycle = (recycleEntryId: string) => {
    const item = rawRecycleBin.find(r => r.id === recycleEntryId || r.product?.id === recycleEntryId);
    if (!item) return;

    const alreadyExists = products.some(p => p.id === item.product.id);
    const restoredProduct: CatalogProduct = alreadyExists
      ? { ...item.product, id: `prod_${Date.now()}`, name: `${item.product.name} (Restored)` }
      : { ...item.product };

    const nextProducts = [restoredProduct, ...products];
    const nextRecycleBin = rawRecycleBin.filter(r => r.id !== recycleEntryId && r.product?.id !== recycleEntryId);

    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          products: nextProducts,
          recycleBin: nextRecycleBin,
        }
      } : n))
    );

    persistNodeDataDirectly({
      products: nextProducts,
      recycleBin: nextRecycleBin,
    });
    setSaveIndicator(`♻️ Restored ${restoredProduct.name}`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handlePermanentDeleteRecycle = (recycleEntryId: string) => {
    const nextRecycleBin = rawRecycleBin.filter(r => r.id !== recycleEntryId && r.product?.id !== recycleEntryId);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          recycleBin: nextRecycleBin,
        }
      } : n))
    );
    persistNodeDataDirectly({ recycleBin: nextRecycleBin });
  };

  const handleEmptyRecycleBin = () => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, recycleBin: [] } } : n))
    );
    persistNodeDataDirectly({ recycleBin: [] });
    setSaveIndicator(`Recycle bin cleared`);
    setTimeout(() => setSaveIndicator(null), 2500);
  };

  const handleRestoreAllRecycle = () => {
    if (rawRecycleBin.length === 0) return;
    const restored = rawRecycleBin.map(r => r.product).filter(Boolean);
    const merged = [...products];
    for (const p of restored) {
      if (!merged.some(m => m.id === p.id)) {
        merged.push(p);
      }
    }
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          products: merged,
          recycleBin: [],
        }
      } : n))
    );
    persistNodeDataDirectly({ products: merged, recycleBin: [] });
    setSaveIndicator(`♻️ Restored all items`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleTakeManualSnapshot = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const newSnapshot: CatalogSnapshot = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: manualSnapshotLabel.trim() || `Manual Snapshot (${products.length} Items)`,
      productCount: products.length,
      products: JSON.parse(JSON.stringify(products)),
      trigger: 'manual',
    };
    const nextSnapshots = [newSnapshot, ...rawSnapshots].slice(0, 25);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          snapshots: nextSnapshots,
        }
      } : n))
    );
    persistNodeDataDirectly({ snapshots: nextSnapshots });
    setManualSnapshotLabel('');
    setIsCreatingSnapshot(false);
    setSaveIndicator(`📸 Snapshot Created`);
    setTimeout(() => setSaveIndicator(null), 3000);
  };

  const handleRestoreSnapshot = (snapId: string) => {
    const targetSnap = rawSnapshots.find(s => s.id === snapId);
    if (!targetSnap || !Array.isArray(targetSnap.products)) return;

    // Create safety pre-rollback snapshot
    const preRollback: CatalogSnapshot = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: `Pre-Rollback State (Restoring: ${targetSnap.label})`,
      productCount: products.length,
      products: JSON.parse(JSON.stringify(products)),
      trigger: 'restore',
    };
    const nextSnapshots = [preRollback, ...rawSnapshots].slice(0, 25);

    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          products: targetSnap.products,
          snapshots: nextSnapshots,
        }
      } : n))
    );

    persistNodeDataDirectly({
      products: targetSnap.products,
      snapshots: nextSnapshots,
    });
    setSaveIndicator(`↺ Restored to "${targetSnap.label}"`);
    setTimeout(() => setSaveIndicator(null), 3500);
  };

  const handleDeleteSnapshot = (snapId: string) => {
    const nextSnapshots = rawSnapshots.filter(s => s.id !== snapId);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? {
        ...n,
        data: {
          ...n.data,
          snapshots: nextSnapshots,
        }
      } : n))
    );
    persistNodeDataDirectly({ snapshots: nextSnapshots });
  };

  const handleStartEdit = (prod: CatalogProduct) => {
    setEditingProd(prod);
    setEditProdName(prod.name);
    const prodCats = getProductCategories(prod);
    setEditProdCategories(prodCats);
    setEditProdCategory(prodCats[0] || 'General');
    setEditProdCategoryTagInput('');
    setEditProdPrice(String(prod.price || ''));
    setEditProdOriginalPrice(prod.originalPrice ? String(prod.originalPrice) : '');
    setEditProdStockQty(prod.stockQuantity !== undefined ? String(prod.stockQuantity) : '50');
    setEditProdUnit(prod.unitType || 'Pieces');
    setEditProdDesc(prod.description || '');
    setEditProdMediaUrl(prod.imageUrl || (prod.media && prod.media[0]?.url) || '');
    setEditProdKeywords(Array.isArray(prod.searchKeywords) ? prod.searchKeywords.join(', ') : '');
    setEditProdAvailable(prod.isAvailable !== false);
    const existingMedia: ProductMediaItem[] = Array.isArray(prod.media) && prod.media.length > 0
      ? [...prod.media]
      : (prod.imageUrl ? [{
          id: `med_${Date.now()}`,
          type: 'image',
          url: prod.imageUrl,
          name: prod.name || 'Product Image'
        }] : []);
    setEditProdMedia(existingMedia);
    setEditProdRawText('');
    setIsAddingProduct(false);
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProd || !editProdName.trim()) return;

    const parsedPrice = parseFloat(editProdPrice) || 0;
    const parsedOriginal = parseFloat(editProdOriginalPrice) || undefined;
    const parsedStock = parseInt(editProdStockQty) || 0;
    const cleanMediaUrl = editProdMediaUrl.trim();
    const mediaItems: ProductMediaItem[] = [...editProdMedia];

    if (cleanMediaUrl && !mediaItems.some(m => m.url === cleanMediaUrl)) {
      const ext = cleanMediaUrl.split('.').pop()?.toLowerCase() || '';
      let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
      if (['mp4', 'mov', 'webm'].includes(ext)) mType = 'video';
      else if (['pdf'].includes(ext)) mType = 'pdf';
      else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';

      mediaItems.unshift({
        id: `med_${Date.now()}`,
        type: mType,
        url: cleanMediaUrl,
        name: `${editProdName.trim()} Media`
      });
    }

    const firstImage = mediaItems.find(m => m.type === 'image');
    const primaryImgUrl = firstImage ? firstImage.url : (cleanMediaUrl || editingProd.imageUrl);

    const keywordList = editProdKeywords
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);

    const finalCategories = editProdCategories.length > 0
      ? editProdCategories
      : [editProdCategory.trim() || 'General'];

    const updated = products.map(p => {
      if (p.id === editingProd.id) {
        return {
          ...p,
          name: editProdName.trim(),
          category: finalCategories[0] || 'General',
          categories: finalCategories,
          price: parsedPrice,
          originalPrice: parsedOriginal,
          stockQuantity: parsedStock,
          unitType: editProdUnit.trim() || 'Pieces',
          isAvailable: editProdAvailable && parsedStock > 0,
          description: editProdDesc.trim(),
          imageUrl: primaryImgUrl,
          media: mediaItems.length > 0 ? mediaItems : undefined,
          searchKeywords: keywordList.length > 0 ? keywordList : undefined,
        };
      }
      return p;
    });

    handleUpdateProducts(updated);
    setEditingProd(null);
    setEditProdMedia([]);
    setEditProdMediaUrl('');
    setEditProdRawText('');
  };

  const handleCreateProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProdName.trim()) return;

    const parsedPrice = parseFloat(newProdPrice) || 0;
    const parsedOriginal = parseFloat(newProdOriginalPrice) || undefined;
    const parsedStock = parseInt(newProdStockQty) || 50;
    const cleanMediaUrl = newProdMediaUrl.trim();
    const mediaItems: ProductMediaItem[] = [...newProdMedia];

    if (cleanMediaUrl && !mediaItems.some(m => m.url === cleanMediaUrl)) {
      const ext = cleanMediaUrl.split('.').pop()?.toLowerCase() || '';
      let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
      if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) mType = 'video';
      else if (['pdf'].includes(ext)) mType = 'pdf';
      else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';

      mediaItems.unshift({
        id: `med_${Date.now()}`,
        type: mType,
        url: cleanMediaUrl,
        name: `${newProdName.trim()} Media`
      });
    }

    const firstImage = mediaItems.find(m => m.type === 'image');
    const primaryImgUrl = firstImage ? firstImage.url : (cleanMediaUrl || undefined);

    const keywordList = newProdKeywords
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);

    const finalCategories = newProdCategories.length > 0
      ? newProdCategories
      : [newProdCategory.trim() || 'General'];

    const newP: CatalogProduct = {
      id: `prod_${Date.now()}`,
      name: newProdName.trim(),
      category: finalCategories[0] || 'General',
      categories: finalCategories,
      price: parsedPrice,
      originalPrice: parsedOriginal,
      minPrice: parsedOriginal ? parsedPrice : undefined,
      maxPrice: parsedPrice,
      stockQuantity: parsedStock,
      currency: defaultCurrency,
      unitType: newProdUnit.trim() || 'Pieces',
      isAvailable: newProdAvailable && parsedStock > 0,
      description: newProdDesc.trim(),
      sku: `SKU-${Math.floor(1000 + Math.random() * 9000)}`,
      imageUrl: primaryImgUrl,
      media: mediaItems.length > 0 ? mediaItems : undefined,
      searchKeywords: keywordList.length > 0 ? keywordList : undefined,
    };

    const updated = [newP, ...products];
    handleUpdateProducts(updated);

    // Reset Form
    setNewProdName('');
    setNewProdCategories(['General']);
    setNewProdCategory('General');
    setNewProdCategoryTagInput('');
    setNewProdPrice('');
    setNewProdOriginalPrice('');
    setNewProdStockQty('50');
    setNewProdDesc('');
    setNewProdMediaUrl('');
    setNewProdKeywords('');
    setNewProdMedia([]);
    setNewProdRawText('');
    setIsAddingProduct(false);
  };

  const renderProductCard = (prod: CatalogProduct) => {
    const thumbUrl = prod.imageUrl || prod.media?.find(m => m.type === 'image')?.url;
    const imageCount = prod.media?.filter(m => m.type === 'image').length || (prod.imageUrl ? 1 : 0);
    const videoCount = prod.media?.filter(m => m.type === 'video').length || 0;
    const docCount = prod.media?.filter(m => m.type === 'pdf' || m.type === 'document').length || 0;
    const stockUnits = prod.stockQuantity !== undefined ? prod.stockQuantity : (prod.isAvailable ? 50 : 0);

    return (
      <div
        key={prod.id}
        className={`p-2.5 rounded-2xl border transition-all ${
          prod.isAvailable && stockUnits > 0
            ? 'bg-slate-900/80 border-slate-800/80 hover:border-emerald-500/40'
            : 'bg-slate-950/40 border-slate-900 opacity-60'
        }`}
      >
        <div className="flex items-start gap-2.5">
          {/* Thumbnail / Media icon */}
          {thumbUrl ? (
            <div className="w-11 h-11 rounded-xl bg-slate-950 border border-emerald-500/30 overflow-hidden shrink-0 relative">
              <img
                src={thumbUrl}
                alt={prod.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="w-11 h-11 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-600 shrink-0">
              <Package size={18} />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-bold text-white truncate max-w-[170px]">
                {prod.name}
              </span>

              {/* Multi-Category Badges */}
              {getProductCategories(prod).map((catName) => (
                <span
                  key={catName}
                  className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded border border-emerald-500/20 flex items-center gap-0.5 shadow-sm"
                >
                  <Folder size={8} className="text-emerald-400" />
                  <span>{catName}</span>
                </span>
              ))}

              {/* Quick Assign / Move Trigger */}
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuickCategoryProdId(quickCategoryProdId === prod.id ? null : prod.id);
                    setQuickNewCategoryInput('');
                  }}
                  className="text-[9px] font-mono font-bold text-emerald-400 hover:text-white bg-emerald-950/60 hover:bg-emerald-900/80 px-1.5 py-0.2 rounded border border-emerald-500/30 flex items-center gap-0.5 transition cursor-pointer"
                  title="Assign or Move Categories"
                >
                  <Tag size={8} />
                  <span>Move</span>
                  <ChevronDown size={8} className={`transition-transform duration-200 ${quickCategoryProdId === prod.id ? 'rotate-180' : ''}`} />
                </button>

                {/* Popover */}
                {quickCategoryProdId === prod.id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="absolute left-0 top-full mt-1.5 w-64 bg-[#0A1A13] border border-emerald-500/40 rounded-xl shadow-2xl p-2.5 z-50 animate-fade-in space-y-2 backdrop-blur-xl"
                  >
                    <div className="flex items-center justify-between pb-1 border-b border-emerald-500/20">
                      <span className="text-[10px] font-mono font-bold text-emerald-300 flex items-center gap-1">
                        <Tag size={10} className="text-emerald-400" />
                        <span>Categories</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuickCategoryProdId(null)}
                        className="text-slate-400 hover:text-white text-xs p-0.5 cursor-pointer"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                      {categories.map((catName) => {
                        const isAssigned = getProductCategories(prod).some(c => c.toLowerCase() === catName.toLowerCase());
                        return (
                          <div
                            key={catName}
                            className={`flex items-center justify-between px-2 py-1 rounded-lg border text-[10px] font-mono transition ${
                              isAssigned
                                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200 font-bold'
                                : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-emerald-500/30 hover:text-slate-200'
                            }`}
                          >
                            <label className="flex items-center gap-1.5 cursor-pointer flex-1 select-none">
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={() => handleToggleProductCategory(prod.id, catName)}
                                className="w-3 h-3 rounded bg-slate-900 border-slate-700 text-emerald-500 cursor-pointer"
                              />
                              <Folder size={10} className={isAssigned ? 'text-emerald-400' : 'text-slate-500'} />
                              <span className="truncate">{catName}</span>
                            </label>

                            {!isAssigned && (
                              <button
                                type="button"
                                onClick={() => handleMoveProductToCategoryOnly(prod.id, catName)}
                                className="text-[8px] font-mono text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 px-1 py-0.5 rounded border border-emerald-500/20 transition cursor-pointer shrink-0 ml-1"
                                title={`Move exclusively to "${catName}"`}
                              >
                                Move
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-1.5 border-t border-emerald-500/20 flex items-center gap-1">
                      <input
                        type="text"
                        value={quickNewCategoryInput}
                        onChange={(e) => setQuickNewCategoryInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (quickNewCategoryInput.trim()) {
                              handleQuickAddAndAssignCategory(prod.id, quickNewCategoryInput);
                            }
                          }
                        }}
                        placeholder="+ New category..."
                        className="flex-1 bg-slate-950 border border-emerald-500/30 rounded-lg px-1.5 py-0.5 text-[10px] text-white placeholder-slate-500 focus:outline-none font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (quickNewCategoryInput.trim()) {
                            handleQuickAddAndAssignCategory(prod.id, quickNewCategoryInput);
                          }
                        }}
                        disabled={!quickNewCategoryInput.trim()}
                        className="px-2 py-0.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 rounded-lg text-[9px] font-mono font-bold transition cursor-pointer"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded border ${
                stockUnits > 0 ? 'text-emerald-300 bg-emerald-950/40 border-emerald-500/30' : 'text-rose-400 bg-rose-950/40 border-rose-500/30'
              }`}>
                Stock: {stockUnits}
              </span>
            </div>

            {prod.description && (
              <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5 font-sans">
                {prod.description}
              </p>
            )}

            {/* Media & Keyword Badges */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {imageCount > 0 && (
                <span className="text-[9px] font-mono text-sky-400 bg-sky-500/10 px-1.5 py-0.2 rounded border border-sky-500/20 flex items-center gap-0.5">
                  <ImageIcon size={9} /> {imageCount}
                </span>
              )}
              {videoCount > 0 && (
                <span className="text-[9px] font-mono text-purple-400 bg-purple-500/10 px-1.5 py-0.2 rounded border border-purple-500/20 flex items-center gap-0.5">
                  <Film size={9} /> {videoCount}
                </span>
              )}
              {docCount > 0 && (
                <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-500/20 flex items-center gap-0.5">
                  <FileText size={9} /> {docCount}
                </span>
              )}
              {Array.isArray(prod.searchKeywords) && prod.searchKeywords.length > 0 && (
                <span className="text-[9px] font-mono text-slate-400 bg-slate-800/80 px-1.5 py-0.2 rounded border border-slate-700/60">
                  {prod.searchKeywords.length} tags
                </span>
              )}
            </div>

            {/* Dynamic Technical Specs & Attributes Vault */}
            {prod.attributes && typeof prod.attributes === 'object' && Object.keys(prod.attributes).length > 0 && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {Object.entries(prod.attributes).map(([k, v]) => (
                  <span key={k} className="text-[8px] font-mono text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20">
                    ⚡ {k}: {String(v)}
                  </span>
                ))}
              </div>
            )}

            {/* Tiered / Bulk Volume Pricing Badges */}
            {prod.tieredPricing && Array.isArray(prod.tieredPricing) && prod.tieredPricing.length > 0 && (
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                <span className="text-[8px] font-mono text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 flex items-center gap-1">
                  📦 Bulk: {prod.tieredPricing.map(t => `${t.minQuantity}+ @ ${prod.currency || defaultCurrency} ${t.unitPrice}`).join(' | ')}
                </span>
              </div>
            )}

            {/* Competitor Price & Advantage Badges */}
            {(prod.competitorPrice || prod.competitiveAdvantage) && (
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {prod.competitorPrice && (
                  <span className="text-[8px] font-mono text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                    ⚖️ Market: {prod.currency || defaultCurrency} {prod.competitorPrice.toLocaleString()}
                  </span>
                )}
                {prod.competitiveAdvantage && (
                  <span className="text-[8px] font-mono text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                    💎 {prod.competitiveAdvantage}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <div className="text-right">
              {prod.originalPrice && prod.originalPrice > prod.price && (
                <span className="text-[9px] text-slate-500 line-through block font-mono">
                  {prod.currency || defaultCurrency} {prod.originalPrice.toLocaleString()}
                </span>
              )}
              <span className="text-xs font-black text-emerald-300 font-mono">
                {prod.currency || defaultCurrency} {prod.price.toLocaleString()}
              </span>
              <span className="text-[9px] text-slate-500 block font-mono">
                /{prod.unitType || 'unit'}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleStartEdit(prod)}
                className="p-1 text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition cursor-pointer"
                title="Edit Product & Stock"
              >
                <Edit3 size={13} />
              </button>

              <button
                type="button"
                onClick={() => handleToggleStock(prod.id)}
                className={`p-1 rounded-lg border transition cursor-pointer ${
                  prod.isAvailable && stockUnits > 0
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20'
                }`}
                title={prod.isAvailable ? 'In Stock (Click to mark Out of Stock)' : 'Out of Stock (Click to enable)'}
              >
                {prod.isAvailable && stockUnits > 0 ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
              </button>

              <button
                type="button"
                onClick={() => handleDeleteProduct(prod.id)}
                className="p-1 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition cursor-pointer"
                title="Delete Product"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`rounded-3xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-2xl bg-gradient-to-b from-[#081813]/98 via-[#05100D]/98 to-[#030806]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-emerald-400 ring-2 ring-emerald-400/50 shadow-[0_0_35px_rgba(16,185,129,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-emerald-500/40 hover:border-emerald-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(16,185,129,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('product_catalog', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border-2 border-emerald-400 text-emerald-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.8)] hover:border-emerald-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view product catalog and manage inventory"
          >
            <ShoppingBag size={13} className="text-emerald-300 animate-pulse" />
            <span>View in Portal</span>
            <ArrowRight size={13} className="text-emerald-400" />
          </button>
        </div>
      )}

      {/* ── Top Target Handle (Upstream / Inbound) ───────────────────── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-catalog"
        className="!bg-emerald-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-20 shadow-[0_0_12px_rgba(16,185,129,0.8)]"
        title="Input: Target Wire"
      />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="p-4 border-b border-emerald-500/30 bg-gradient-to-r from-emerald-950/80 via-teal-950/60 to-slate-950/80 rounded-t-3xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-400 shadow-inner">
            <ShoppingBag size={20} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-white tracking-wide font-sans">
                Product Catalog & Menu
              </h3>
              <span className="text-[9px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded-full">
                LIVE STORE
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              {products.length} Products • {products.filter(p => p.isAvailable).length} In-Stock
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono text-emerald-400 animate-fade-in bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
              {saveIndicator}
            </span>
          )}
        </div>
      </div>

      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-emerald-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingBag size={14} className="text-emerald-400" />
                <span className="font-bold text-white text-xs">
                  {products.length} Products Configured
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-200 border border-emerald-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-emerald-300 font-bold">
                🛍️ {products.filter(p => p.isAvailable).length} Available
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                {categories.length} Categories
              </span>
              <span className="px-2 py-0.5 rounded-md bg-emerald-950/70 border border-emerald-500/30 text-[10px] font-mono text-emerald-300">
                Autonomous Cart
              </span>
            </div>
          </div>
        </div>
      ) : (
        <>
      {/* ── Order Engine & Courier Tracking HUD ──────────────────────── */}
      <div className="px-4 py-2 bg-emerald-950/40 border-b border-emerald-500/20 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Truck size={12} className="text-emerald-400" />
          <span className="text-[10px] font-bold text-emerald-300 font-mono tracking-wide">
            Autonomous Orders & Tracking
          </span>
          {enableOrderTaking && (
            <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Orders ON
            </span>
          )}
          {enableOrderTracking && (
            <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-teal-500/20 text-teal-300 border border-teal-500/30">
              Tracking ON
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowOrderSettings(!showOrderSettings)}
            className="text-[9px] font-mono px-2 py-0.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition cursor-pointer"
          >
            {showOrderSettings ? 'Hide ✕' : 'Configure ⚙️'}
          </button>
          {openPortalData && (
            <button
              type="button"
              onClick={() => openPortalData('product_catalog')}
              className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-200 border border-teal-500/40 transition cursor-pointer flex items-center gap-1 shadow-sm"
              title="Open Orders & Live Courier Tracking Studio"
            >
              <span>Orders</span>
              <ArrowRight size={10} />
            </button>
          )}
        </div>
      </div>

      {/* ── Zero-Token Pre-filled Messages & Gatekeeper HUD ──────────── */}
      <div className="px-4 py-2 bg-teal-950/40 border-b border-teal-500/20 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Zap size={12} className="text-teal-400" />
          <span className="text-[10px] font-bold text-teal-300 font-mono tracking-wide">
            Zero-Token Instant Quick-Replies
          </span>
          {preFilledTemplatesEnabled ? (
            <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Active ({preFilledTemplates.filter(t => t.isEnabled).length}/{preFilledTemplates.length})
            </span>
          ) : (
            <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-800 text-slate-400 border border-slate-700">
              Disabled
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowPreFilledDrawer(!showPreFilledDrawer)}
            className={`text-[9px] font-mono px-2 py-0.5 rounded-lg transition cursor-pointer border flex items-center gap-1 ${
              showPreFilledDrawer
                ? 'bg-teal-500 text-slate-950 border-teal-400 font-bold shadow-teal-500/20'
                : 'bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border-teal-500/30'
            }`}
          >
            <Zap size={10} />
            <span>{showPreFilledDrawer ? 'Hide Quick Replies ✕' : `Configure Quick Replies (${preFilledTemplates.length}) ⚡`}</span>
          </button>
        </div>
      </div>

      {/* ── Order Engine Settings Drawer ─────────────────────────────── */}
      {showOrderSettings && (
        <div className="p-3 bg-slate-950/90 border-b border-emerald-500/20 space-y-2.5 text-xs animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold text-white flex items-center gap-1">
                <ClipboardCheck size={12} className="text-emerald-400" /> Luna Autonomous Order Taking
              </div>
              <div className="text-[9px] text-slate-400">Takes delivery orders & notifies owner dispatch</div>
            </div>
            <input
              type="checkbox"
              checked={enableOrderTaking}
              onChange={(e) => {
                const val = e.target.checked;
                setEnableOrderTaking(val);
                persistNodeDataDirectly({ enableOrderTaking: val });
              }}
              className="rounded accent-emerald-500 cursor-pointer w-4 h-4"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold text-white flex items-center gap-1">
                <Truck size={12} className="text-teal-400" /> Live Courier & Parcel Tracking
              </div>
              <div className="text-[9px] text-slate-400">Reports live status & courier tracking code to customers</div>
            </div>
            <input
              type="checkbox"
              checked={enableOrderTracking}
              onChange={(e) => {
                const val = e.target.checked;
                setEnableOrderTracking(val);
                persistNodeDataDirectly({ enableOrderTracking: val });
              }}
              className="rounded accent-teal-500 cursor-pointer w-4 h-4"
            />
          </div>

          <div>
            <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
              Store Owner WhatsApp Alert Phone
            </label>
            <input
              type="text"
              value={orderNotificationPhone}
              onChange={(e) => {
                const val = e.target.value;
                setOrderNotificationPhone(val);
                persistNodeDataDirectly({ orderNotificationPhone: val });
              }}
              placeholder="e.g. 94771234567 (Optional fallback destination)"
              className="w-full bg-slate-900 border border-slate-700 focus:border-emerald-400 rounded-lg px-2 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
            />
          </div>

          {/* Order Intake Form & Courier Templates Trigger */}
          <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold text-white flex items-center gap-1">
                <FileText size={12} className="text-amber-400" /> Order Intake & Courier Templates
              </div>
              <div className="text-[9px] text-slate-400">Customize fields, 2 phone numbers & 1-tap courier forwarding</div>
            </div>
            <button
              type="button"
              onClick={() => setShowTemplateModal(!showTemplateModal)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 transition shadow-sm cursor-pointer border ${
                showTemplateModal
                  ? 'bg-amber-500 text-slate-950 border-amber-400 shadow-amber-500/30'
                  : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border-amber-500/30'
              }`}
            >
              <FileText size={11} />
              <span>{showTemplateModal ? 'Hide Templates ✕' : 'Configure Templates ⚙️'}</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Order Intake & Courier Forwarding Template Editor Drawer ───── */}
      {showTemplateModal && (
        <div className="p-3.5 bg-amber-950/20 border-b border-amber-500/30 space-y-3 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <FileText size={13} className="text-amber-400" />
              <span className="text-[11px] font-black font-mono text-amber-300 uppercase tracking-wider">
                Order Intake Form & Courier Dispatch Studio
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowTemplateModal(false)}
              className="text-slate-400 hover:text-slate-200 text-xs p-1 cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* Sub-Tabs: Fields, Courier Template, Customer Template, Preview */}
          <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-white/5">
            <button
              type="button"
              onClick={() => setTemplateActiveTab('fields')}
              className={`flex-1 py-1 px-2 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1 cursor-pointer ${
                templateActiveTab === 'fields'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <ClipboardCheck size={11} />
              <span>Intake Fields</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplateActiveTab('owner_alert')}
              className={`flex-1 py-1 px-2 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1 cursor-pointer ${
                templateActiveTab === 'owner_alert'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Bell size={11} />
              <span>Owner Alert</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplateActiveTab('courier')}
              className={`flex-1 py-1 px-2 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1 cursor-pointer ${
                templateActiveTab === 'courier'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Truck size={11} />
              <span>Courier Template</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplateActiveTab('customer')}
              className={`flex-1 py-1 px-2 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1 cursor-pointer ${
                templateActiveTab === 'customer'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Smartphone size={11} />
              <span>Customer Receipt</span>
            </button>
            <button
              type="button"
              onClick={() => setTemplateActiveTab('preview')}
              className={`flex-1 py-1 px-2 rounded-lg text-[10px] font-bold transition flex items-center justify-center gap-1 cursor-pointer ${
                templateActiveTab === 'preview'
                  ? 'bg-amber-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Eye size={11} />
              <span>Live Preview</span>
            </button>
          </div>

          {/* TAB 1: Intake Fields Customizer */}
          {templateActiveTab === 'fields' && (
            <div className="space-y-3 animate-fade-in text-xs">
              <div className="text-[10px] text-slate-300">
                Select which customer details Luna must gather before confirming the order.
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* 1. Customer Name */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">1. Full Name</div>
                    <div className="text-[9px] text-slate-400">Customer / Recipient Name</div>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-500/30 font-mono">
                    Mandatory
                  </span>
                </div>

                {/* 2. Delivery Address */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">2. Delivery Address</div>
                    <div className="text-[9px] text-slate-400">Doorstep street address</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.deliveryAddress !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, deliveryAddress: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 3. Nearest City / Town */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">3. City / Nearest Town</div>
                    <div className="text-[9px] text-slate-400">Required for courier parcel routing</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.city !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, city: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 4. Primary Phone Number */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">4. Primary Phone Number</div>
                    <div className="text-[9px] text-slate-400">Main contact number for delivery</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.primaryPhone !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, primaryPhone: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 5. Secondary Phone Number (Courier Backup) */}
                <div className="p-2 bg-amber-950/30 border border-amber-500/30 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-amber-200 text-[11px] flex items-center gap-1">
                      <span>5. 2nd Phone Number (Courier Backup)</span>
                    </div>
                    <div className="text-[9px] text-slate-300">Rider calls backup if primary doesn't answer</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.secondaryPhone !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, secondaryPhone: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 6. Working WhatsApp Number (Smart Fallback) */}
                <div className="p-2 bg-emerald-950/30 border border-emerald-500/30 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-emerald-200 text-[11px] flex items-center gap-1">
                      <span>6. WhatsApp Number (Smart Fallback)</span>
                    </div>
                    <div className="text-[9px] text-emerald-300/80">
                      ⚡ Grabs current chatting WhatsApp if user says "same" / none provided
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.whatsappNumber !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, whatsappNumber: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-emerald-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 7. Landmark / Location Hint */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">7. Landmark / Location Hint</div>
                    <div className="text-[9px] text-slate-400">e.g. Near clock tower, Opposite temple</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.landmark === true}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, landmark: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>

                {/* 8. Delivery Notes */}
                <div className="p-2 bg-slate-900/90 border border-slate-800 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white text-[11px]">8. Special Delivery Notes</div>
                    <div className="text-[9px] text-slate-400">Customer requests / time preferences</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={orderTemplateConfig.fields?.notes !== false}
                    onChange={(e) => {
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, notes: e.target.checked }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="rounded accent-amber-500 cursor-pointer w-4 h-4"
                  />
                </div>
              </div>

              {/* Custom Fields Manager */}
              <div className="pt-2 border-t border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase font-bold text-slate-400">
                    Additional Custom Fields ({(orderTemplateConfig.fields?.customFields || []).length})
                  </span>
                </div>

                {(orderTemplateConfig.fields?.customFields || []).map((cf: any, idx: number) => (
                  <div key={idx} className="p-2 bg-slate-900 rounded-lg border border-slate-800 flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-amber-400">{cf.key}</span>
                      <span className="text-white font-bold">— {cf.label}</span>
                      {cf.required && (
                        <span className="text-[8px] bg-rose-950 text-rose-300 px-1.5 py-0.2 rounded border border-rose-500/30">
                          Required
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const nextFields = (orderTemplateConfig.fields?.customFields || []).filter((_: any, i: number) => i !== idx);
                        const updated = {
                          ...orderTemplateConfig,
                          fields: { ...orderTemplateConfig.fields, customFields: nextFields }
                        };
                        setOrderTemplateConfig(updated);
                        persistNodeDataDirectly({ orderTemplateConfig: updated });
                      }}
                      className="text-slate-400 hover:text-rose-400 transition"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <input
                    type="text"
                    placeholder="Field key (e.g. nic_number)"
                    value={newCustomFieldKey}
                    onChange={(e) => setNewCustomFieldKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="flex-1 min-w-[120px] bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-white placeholder-slate-500 font-mono"
                  />
                  <input
                    type="text"
                    placeholder="Display label (e.g. NIC Number)"
                    value={newCustomFieldLabel}
                    onChange={(e) => setNewCustomFieldLabel(e.target.value)}
                    className="flex-1 min-w-[120px] bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-white placeholder-slate-500"
                  />
                  <label className="flex items-center gap-1 text-[10px] text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newCustomFieldRequired}
                      onChange={(e) => setNewCustomFieldRequired(e.target.checked)}
                      className="rounded accent-amber-500 w-3.5 h-3.5"
                    />
                    <span>Required</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newCustomFieldKey.trim() || !newCustomFieldLabel.trim()) return;
                      const nextFields = [
                        ...(orderTemplateConfig.fields?.customFields || []),
                        { key: newCustomFieldKey.trim(), label: newCustomFieldLabel.trim(), required: newCustomFieldRequired }
                      ];
                      const updated = {
                        ...orderTemplateConfig,
                        fields: { ...orderTemplateConfig.fields, customFields: nextFields }
                      };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                      setNewCustomFieldKey('');
                      setNewCustomFieldLabel('');
                      setNewCustomFieldRequired(false);
                    }}
                    className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-[10px] flex items-center gap-1 transition cursor-pointer"
                  >
                    <Plus size={11} /> Add Field
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Owner Order Alert Template */}
          {templateActiveTab === 'owner_alert' && (
            <div className="space-y-2.5 animate-fade-in text-xs">
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-slate-300">
                  First alert sent to registered owner WhatsApp with full items breakdown & active customer WhatsApp.
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, ownerConfirmTemplate: '' };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                    title="Clear all text"
                  >
                    <Trash2 size={10} /> Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, ownerConfirmTemplate: DEFAULT_OWNER_CONFIRM_TEMPLATE };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                  >
                    <RotateCcw size={10} /> Reset Default
                  </button>
                </div>
              </div>

              {/* Variable Chips */}
              <div>
                <span className="text-[9px] font-mono font-bold text-slate-400 block mb-1 uppercase">Click to Insert Dynamic Variable:</span>
                <div className="flex flex-wrap gap-1">
                  {[
                    'orderNumber', 'storeName', 'orderType', 'customerName', 'deliveryAddress', 'city',
                    'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                    'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                  ].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const tag = `{{${v}}}`;
                        const cur = orderTemplateConfig.ownerConfirmTemplate || '';
                        const updated = { ...orderTemplateConfig, ownerConfirmTemplate: cur ? `${cur} ${tag}` : tag };
                        setOrderTemplateConfig(updated);
                        persistNodeDataDirectly({ orderTemplateConfig: updated });
                      }}
                      className="px-1.5 py-0.5 bg-slate-900 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 rounded text-[9px] font-mono transition cursor-pointer"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                rows={9}
                value={orderTemplateConfig.ownerConfirmTemplate ?? ''}
                placeholder="Type your owner order alert template here or click clear to start from blank..."
                onChange={(e) => {
                  const val = e.target.value;
                  const updated = { ...orderTemplateConfig, ownerConfirmTemplate: val };
                  setOrderTemplateConfig(updated);
                  persistNodeDataDirectly({ orderTemplateConfig: updated });
                }}
                className="w-full bg-slate-950 border border-slate-700 focus:border-amber-400 rounded-xl p-2.5 font-mono text-[11px] text-white leading-relaxed focus:outline-none resize-y"
              />
            </div>
          )}

          {/* TAB 3: Courier Forwarding Template */}
          {templateActiveTab === 'courier' && (
            <div className="space-y-2.5 animate-fade-in text-xs">
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-slate-300">
                  Formatted for 1-tap forwarding to Courier services (Koombiyo, Prompt, Domex, Pronto, Fardar).
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, ownerCourierTemplate: '' };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                    title="Clear all text"
                  >
                    <Trash2 size={10} /> Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, ownerCourierTemplate: DEFAULT_COURIER_TEMPLATE };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                  >
                    <RotateCcw size={10} /> Reset Default
                  </button>
                </div>
              </div>

              {/* Variable Chips */}
              <div>
                <span className="text-[9px] font-mono font-bold text-slate-400 block mb-1 uppercase">Click to Insert Dynamic Variable:</span>
                <div className="flex flex-wrap gap-1">
                  {[
                    'orderNumber', 'storeName', 'customerName', 'deliveryAddress', 'city',
                    'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                    'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                  ].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const tag = `{{${v}}}`;
                        const cur = orderTemplateConfig.ownerCourierTemplate || '';
                        const updated = { ...orderTemplateConfig, ownerCourierTemplate: cur ? `${cur} ${tag}` : tag };
                        setOrderTemplateConfig(updated);
                        persistNodeDataDirectly({ orderTemplateConfig: updated });
                      }}
                      className="px-1.5 py-0.5 bg-slate-900 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 rounded text-[9px] font-mono transition cursor-pointer"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                rows={9}
                value={orderTemplateConfig.ownerCourierTemplate ?? ''}
                placeholder="Type your courier forwarding template here or click clear to start from blank..."
                onChange={(e) => {
                  const val = e.target.value;
                  const updated = { ...orderTemplateConfig, ownerCourierTemplate: val };
                  setOrderTemplateConfig(updated);
                  persistNodeDataDirectly({ orderTemplateConfig: updated });
                }}
                className="w-full bg-slate-950 border border-slate-700 focus:border-amber-400 rounded-xl p-2.5 font-mono text-[11px] text-white leading-relaxed focus:outline-none resize-y"
              />
            </div>
          )}

          {/* TAB 3: Customer Confirmation Receipt Template */}
          {templateActiveTab === 'customer' && (
            <div className="space-y-2.5 animate-fade-in text-xs">
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-slate-300">
                  Confirmation receipt message sent to the customer on WhatsApp once order is placed.
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, customerConfirmationTemplate: '' };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                    title="Clear all text"
                  >
                    <Trash2 size={10} /> Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = { ...orderTemplateConfig, customerConfirmationTemplate: DEFAULT_CUSTOMER_RECEIPT_TEMPLATE };
                      setOrderTemplateConfig(updated);
                      persistNodeDataDirectly({ orderTemplateConfig: updated });
                    }}
                    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-mono flex items-center gap-1 cursor-pointer"
                  >
                    <RotateCcw size={10} /> Reset Default
                  </button>
                </div>
              </div>

              {/* Variable Chips */}
              <div>
                <span className="text-[9px] font-mono font-bold text-slate-400 block mb-1 uppercase">Click to Insert Dynamic Variable:</span>
                <div className="flex flex-wrap gap-1">
                  {[
                    'orderNumber', 'storeName', 'customerName', 'deliveryAddress', 'city',
                    'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                    'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                  ].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const tag = `{{${v}}}`;
                        const cur = orderTemplateConfig.customerConfirmationTemplate || '';
                        const updated = { ...orderTemplateConfig, customerConfirmationTemplate: cur ? `${cur} ${tag}` : tag };
                        setOrderTemplateConfig(updated);
                        persistNodeDataDirectly({ orderTemplateConfig: updated });
                      }}
                      className="px-1.5 py-0.5 bg-slate-900 hover:bg-teal-500/20 text-teal-300 border border-teal-500/20 rounded text-[9px] font-mono transition cursor-pointer"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                rows={9}
                value={orderTemplateConfig.customerConfirmationTemplate ?? ''}
                placeholder="Type your customer receipt template here or click clear to start from blank..."
                onChange={(e) => {
                  const val = e.target.value;
                  const updated = { ...orderTemplateConfig, customerConfirmationTemplate: val };
                  setOrderTemplateConfig(updated);
                  persistNodeDataDirectly({ orderTemplateConfig: updated });
                }}
                className="w-full bg-slate-950 border border-slate-700 focus:border-teal-400 rounded-xl p-2.5 font-mono text-[11px] text-white leading-relaxed focus:outline-none resize-y"
              />
            </div>
          )}

          {/* TAB 4: Live Preview */}
          {templateActiveTab === 'preview' && (
            <div className="space-y-2.5 animate-fade-in text-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('owner_alert')}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition ${
                      previewMode === 'owner_alert' ? 'bg-amber-500 text-slate-950 font-black shadow-sm' : 'bg-slate-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    Owner Order Alert
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('courier')}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition ${
                      previewMode === 'courier' ? 'bg-amber-500 text-slate-950 font-black shadow-sm' : 'bg-slate-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    Courier Forward Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('customer')}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition ${
                      previewMode === 'customer' ? 'bg-teal-500 text-slate-950 font-black shadow-sm' : 'bg-slate-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    Customer Receipt
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const sampleText = previewMode === 'owner_alert'
                      ? (orderTemplateConfig.ownerConfirmTemplate || DEFAULT_OWNER_CONFIRM_TEMPLATE)
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*orderType\s*\}\}/gi, 'Delivery Order')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                      : previewMode === 'courier'
                      ? (orderTemplateConfig.ownerCourierTemplate || DEFAULT_COURIER_TEMPLATE)
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                      : (orderTemplateConfig.customerConfirmationTemplate || DEFAULT_CUSTOMER_RECEIPT_TEMPLATE)
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '1x Rose Bouquet (LKR 4,500), 1x Chocolates Box (LKR 2,200)')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM');
                    navigator.clipboard.writeText(sampleText);
                    setCopiedPreview(true);
                    setTimeout(() => setCopiedPreview(false), 2000);
                  }}
                  className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] flex items-center gap-1 cursor-pointer"
                >
                  {copiedPreview ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                  <span>{copiedPreview ? 'Copied!' : 'Copy Sample'}</span>
                </button>
              </div>

              {/* Chat Bubble Rendering */}
              <div className="p-3.5 rounded-2xl bg-[#0b141a] border border-[#1f2c34] text-slate-200 font-sans text-xs shadow-inner whitespace-pre-wrap leading-relaxed">
                {previewMode === 'owner_alert'
                  ? (orderTemplateConfig.ownerConfirmTemplate || DEFAULT_OWNER_CONFIRM_TEMPLATE)
                      .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                      .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                      .replace(/\{\{\s*orderType\s*\}\}/gi, 'Delivery Order')
                      .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                      .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                      .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                      .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                      .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                      .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                      .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                      .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                      .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                      .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                      .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                      .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                  : previewMode === 'courier'
                  ? (orderTemplateConfig.ownerCourierTemplate || DEFAULT_COURIER_TEMPLATE)
                      .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                      .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                      .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                      .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                      .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                      .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                      .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                      .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                      .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                      .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                      .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                      .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                      .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                      .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                  : (orderTemplateConfig.customerConfirmationTemplate || DEFAULT_CUSTOMER_RECEIPT_TEMPLATE)
                      .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                      .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                      .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                      .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                      .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                      .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                      .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                      .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                      .replace(/\{\{\s*itemsList\s*\}\}/gi, '1x Rose Bouquet (LKR 4,500), 1x Chocolates Box (LKR 2,200)')
                      .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                      .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                      .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                      .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM')
                      .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Zero-Token Pre-filled Messages & Gatekeeper Drawer ─────────── */}
      {showPreFilledDrawer && (
        <div className="p-3.5 bg-teal-950/20 border-b border-teal-500/30 space-y-3 animate-fade-in text-xs">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-teal-400" />
              <span className="text-[11px] font-black font-mono text-teal-300 uppercase tracking-wider">
                Zero-Token Pre-filled Messages & Greeting Gatekeeper
              </span>
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                100% Token Free
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowPreFilledDrawer(false)}
              className="text-slate-400 hover:text-slate-200 text-xs p-1 cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* Master Toggle & Value Proposition Banner */}
          <div className="p-3 rounded-xl bg-slate-900/80 border border-teal-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold text-white flex items-center gap-1.5">
                <span>Enable Zero-Token Pre-filled Responses</span>
                {preFilledTemplatesEnabled ? (
                  <span className="text-[9px] text-emerald-400 font-mono">● Active</span>
                ) : (
                  <span className="text-[9px] text-slate-500 font-mono">○ Inactive</span>
                )}
              </div>
              <p className="text-[10px] text-slate-300 mt-0.5 leading-relaxed">
                When customers send greetings (<code className="text-teal-300 font-mono">hi, hello</code>) or ask matching product questions, Luna immediately dispatches this pre-filled message at <strong>0 LLM tokens</strong>. Conversation automatically transitions to Luna live AI from message #2 onwards!
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer shrink-0">
              <span className="text-[10px] text-slate-300 font-medium">{preFilledTemplatesEnabled ? 'Active' : 'Bypassed'}</span>
              <input
                type="checkbox"
                checked={preFilledTemplatesEnabled}
                onChange={(e) => {
                  const val = e.target.checked;
                  setPreFilledTemplatesEnabled(val);
                  persistNodeDataDirectly({ preFilledTemplatesEnabled: val });
                }}
                className="rounded accent-emerald-500 cursor-pointer w-4 h-4"
              />
            </label>
          </div>

          {/* If Editing a Template or Creating New */}
          {editingTemplateId !== null ? (
            <div className="p-3.5 bg-slate-950/90 rounded-2xl border border-teal-500/40 space-y-3 animate-fade-in">
              <div className="flex items-center justify-between pb-2 border-b border-white/5">
                <div className="flex items-center gap-1.5">
                  <Edit3 size={13} className="text-teal-400" />
                  <span className="text-[11px] font-bold text-white">
                    {editingTemplateId === 'new' ? 'Create New Pre-filled Template' : 'Edit Template'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => { setEditingTemplateId(null); setTemplateDraft({}); }}
                  className="text-slate-400 hover:text-slate-200 text-xs px-2 py-0.5 rounded bg-slate-800"
                >
                  Cancel
                </button>
              </div>

              {/* Individual Active / Armed Toggle Switch */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-200">Template Status:</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1.5 ${
                    templateDraft.isEnabled !== false
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}>
                    {templateDraft.isEnabled !== false ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        <span>⚡ Active (Gatekeeper Armed)</span>
                      </>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                        <span>⏸ Paused (Bypassed)</span>
                      </>
                    )}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateDraft.isEnabled !== false}
                    onChange={(e) => setTemplateDraft({ ...templateDraft, isEnabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>

              {/* Template Title */}
              <div>
                <label className="text-[10px] font-bold text-slate-300 block mb-1">
                  Template Title / Internal Reference
                </label>
                <input
                  type="text"
                  placeholder="e.g. Denim Jacket Quick Overview or Welcome Greeting"
                  value={templateDraft.title || ''}
                  onChange={(e) => setTemplateDraft({ ...templateDraft, title: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 focus:border-teal-400 rounded-xl px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
                />
              </div>

              {/* Trigger Type Selection */}
              <div>
                <label className="text-[10px] font-bold text-slate-300 block mb-1.5">
                  Trigger Condition (When Should This Send?)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateDraft({
                        ...templateDraft,
                        triggerType: 'first_contact_greeting',
                        triggerKeywords: ['hi', 'hello', 'hey', 'start', 'wisthara', 'details', 'help', 'info', 'ආයුබෝවන්', 'හලෝ']
                      });
                      setKeywordInputDraft('hi, hello, hey, start, wisthara, details, help, info, ආයුබෝවන්, හලෝ');
                    }}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      templateDraft.triggerType === 'first_contact_greeting'
                        ? 'bg-sky-500/20 border-sky-400 text-sky-200 shadow-sm'
                        : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="font-bold text-[11px] flex items-center gap-1.5">
                      <span>👋</span>
                      <span>1st Contact Greeting</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                      First customer incoming message or greetings (hi, hello, hey)
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTemplateDraft({ ...templateDraft, triggerType: 'keyword_match' })}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      templateDraft.triggerType === 'keyword_match'
                        ? 'bg-amber-500/20 border-amber-400 text-amber-200 shadow-sm'
                        : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="font-bold text-[11px] flex items-center gap-1.5">
                      <span>🔍</span>
                      <span>Keywords / Questions</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                      Customer asks specific phrases (e.g. &quot;denim&quot;, &quot;delivery time&quot;)
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTemplateDraft({ ...templateDraft, triggerType: 'product_inquiry' })}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      templateDraft.triggerType === 'product_inquiry'
                        ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200 shadow-sm'
                        : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="font-bold text-[11px] flex items-center gap-1.5">
                      <span>🛍️</span>
                      <span>Product Inquiry</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-1 leading-tight">
                      Direct inquiry about specific catalog products
                    </div>
                  </button>
                </div>
              </div>

              {/* Trigger Keywords Input */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold text-slate-300">
                    Trigger Keywords / Questions (Comma Separated)
                  </label>
                  <span className="text-[9px] text-slate-400">Match is case-insensitive</span>
                </div>
                <input
                  type="text"
                  placeholder="e.g. denim, denim jacket, can i get more info about denim"
                  value={keywordInputDraft}
                  onChange={(e) => {
                    const text = e.target.value;
                    setKeywordInputDraft(text);
                    const parsed = text.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                    setTemplateDraft({ ...templateDraft, triggerKeywords: parsed });
                  }}
                  className="w-full bg-slate-900 border border-slate-700 focus:border-teal-400 rounded-xl px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                />
                {/* Quick Add Keyword Chips */}
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className="text-[9px] text-slate-500 self-center">Quick Add:</span>
                  <button
                    type="button"
                    onClick={() => {
                      const add = ['hi', 'hello', 'hey', 'start', 'wisthara'];
                      const current = templateDraft.triggerKeywords || [];
                      const combined = Array.from(new Set([...current, ...add]));
                      setKeywordInputDraft(combined.join(', '));
                      setTemplateDraft({ ...templateDraft, triggerKeywords: combined });
                    }}
                    className="text-[9px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                  >
                    + Greetings
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const add = ['price', 'cost', 'gana', 'keeyada'];
                      const current = templateDraft.triggerKeywords || [];
                      const combined = Array.from(new Set([...current, ...add]));
                      setKeywordInputDraft(combined.join(', '));
                      setTemplateDraft({ ...templateDraft, triggerKeywords: combined });
                    }}
                    className="text-[9px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                  >
                    + Pricing
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const add = ['details', 'info', 'wisthara', 'more info'];
                      const current = templateDraft.triggerKeywords || [];
                      const combined = Array.from(new Set([...current, ...add]));
                      setKeywordInputDraft(combined.join(', '));
                      setTemplateDraft({ ...templateDraft, triggerKeywords: combined });
                    }}
                    className="text-[9px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                  >
                    + Details / Info
                  </button>
                </div>
              </div>

              {/* 🛍️ MULTI-PRODUCT SELECTION & MULTI-PHOTO GALLERY PER PRODUCT */}
              {(() => {
                const rawProductIds: string[] = Array.isArray(templateDraft.attachedProductIds) && templateDraft.attachedProductIds.length > 0
                  ? templateDraft.attachedProductIds
                  : (templateDraft.attachedProductId ? [templateDraft.attachedProductId] : []);

                const handleAddProduct = (prodId: string) => {
                  if (!prodId || rawProductIds.includes(prodId)) return;
                  const nextIds = [...rawProductIds, prodId];
                  const prod = products.find(p => p.id === prodId);
                  const prodImgs: string[] = [];
                  if (prod?.imageUrl) prodImgs.push(prod.imageUrl);
                  if (Array.isArray(prod?.media)) {
                    prod.media.forEach((m: any) => {
                      const u = typeof m === 'string' ? m : m?.url;
                      if (u && !prodImgs.includes(u)) prodImgs.push(u);
                    });
                  }
                  const nextSelectedImages = {
                    ...(templateDraft.selectedProductImages || {}),
                    [prodId]: prodImgs
                  };
                  setTemplateDraft({
                    ...templateDraft,
                    attachedProductIds: nextIds,
                    attachedProductId: nextIds[0],
                    selectedProductImages: nextSelectedImages,
                    attachedMediaUrl: !templateDraft.attachedMediaUrl && prod?.imageUrl ? prod.imageUrl : templateDraft.attachedMediaUrl
                  });
                };

                const handleRemoveProduct = (prodId: string) => {
                  const nextIds = rawProductIds.filter(id => id !== prodId);
                  const nextSelectedImages = { ...(templateDraft.selectedProductImages || {}) };
                  delete nextSelectedImages[prodId];
                  setTemplateDraft({
                    ...templateDraft,
                    attachedProductIds: nextIds,
                    attachedProductId: nextIds[0] || undefined,
                    selectedProductImages: nextSelectedImages
                  });
                };

                const handleToggleProductImage = (prodId: string, imgUrl: string) => {
                  const currentImages = templateDraft.selectedProductImages?.[prodId] || [];
                  let updatedImages: string[];
                  if (currentImages.includes(imgUrl)) {
                    updatedImages = currentImages.filter(u => u !== imgUrl);
                  } else {
                    updatedImages = [...currentImages, imgUrl];
                  }
                  setTemplateDraft({
                    ...templateDraft,
                    selectedProductImages: {
                      ...(templateDraft.selectedProductImages || {}),
                      [prodId]: updatedImages
                    }
                  });
                };

                return (
                  <div className="space-y-2 pt-2 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-300 flex items-center gap-1.5">
                        <ShoppingBag size={12} className="text-teal-400" />
                        <span>Attach Catalog Products (Multiple Supported)</span>
                      </label>
                      <span className="text-[9px] text-teal-400 font-mono">
                        {rawProductIds.length} Product{rawProductIds.length === 1 ? '' : 's'} Selected
                      </span>
                    </div>

                    {/* Product Dropdown to Add */}
                    <div className="flex items-center gap-2">
                      <select
                        value=""
                        onChange={(e) => handleAddProduct(e.target.value)}
                        className="flex-1 bg-slate-900 border border-slate-700 focus:border-teal-400 rounded-xl px-2.5 py-1.5 text-xs text-white focus:outline-none cursor-pointer"
                      >
                        <option value="">-- Click to select & add a product to this template --</option>
                        {products
                          .filter(p => !rawProductIds.includes(p.id))
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              + {p.name} ({p.currency || defaultCurrency} {p.price?.toLocaleString()})
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Selected Products List & Photo Selector */}
                    {rawProductIds.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {rawProductIds.map((pid) => {
                          const prod = products.find(x => x.id === pid);
                          if (!prod) return null;

                          // Collect all images for this product (primary + gallery)
                          const allProdImages: string[] = [];
                          if (prod.imageUrl) allProdImages.push(prod.imageUrl);
                          if (Array.isArray(prod.media)) {
                            prod.media.forEach((m: any) => {
                              const u = typeof m === 'string' ? m : m?.url;
                              if (u && !allProdImages.includes(u)) allProdImages.push(u);
                            });
                          }

                          const selectedImgs = templateDraft.selectedProductImages?.[prod.id] || (prod.imageUrl ? [prod.imageUrl] : allProdImages);

                          return (
                            <div key={pid} className="p-2.5 rounded-xl bg-slate-900/90 border border-teal-500/30 space-y-2">
                              {/* Product Header */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 overflow-hidden flex-1 min-w-0">
                                  {prod.imageUrl ? (
                                    <img src={prod.imageUrl} alt={prod.name} className="w-8 h-8 rounded object-cover border border-teal-500/30 shrink-0" />
                                  ) : (
                                    <Package size={16} className="text-teal-400 shrink-0" />
                                  )}
                                  <div className="truncate">
                                    <span className="text-[11px] font-bold text-white truncate block">{prod.name}</span>
                                    <span className="text-[9px] text-teal-400 font-mono">
                                      {prod.currency || defaultCurrency} {prod.price?.toLocaleString()}
                                      {prod.category ? ` • ${prod.category}` : ''}
                                    </span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveProduct(pid)}
                                  className="text-slate-400 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition cursor-pointer"
                                  title="Remove Product"
                                >
                                  <X size={13} />
                                </button>
                              </div>

                              {/* Multi-Photo Checklist for this product */}
                              {allProdImages.length > 0 && (
                                <div className="pt-1.5 border-t border-white/5 space-y-1.5">
                                  <div className="flex items-center justify-between text-[9px]">
                                    <span className="text-slate-300 font-medium flex items-center gap-1">
                                      <Camera size={10} className="text-teal-400" />
                                      <span>Select Photos to Send ({selectedImgs.length}/{allProdImages.length}):</span>
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setTemplateDraft({
                                            ...templateDraft,
                                            selectedProductImages: {
                                              ...(templateDraft.selectedProductImages || {}),
                                              [prod.id]: allProdImages
                                            }
                                          });
                                        }}
                                        className="text-[8px] text-teal-300 hover:underline cursor-pointer"
                                      >
                                        All Photos
                                      </button>
                                      <span className="text-slate-600">•</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setTemplateDraft({
                                            ...templateDraft,
                                            selectedProductImages: {
                                              ...(templateDraft.selectedProductImages || {}),
                                              [prod.id]: prod.imageUrl ? [prod.imageUrl] : allProdImages.slice(0, 1)
                                            }
                                          });
                                        }}
                                        className="text-[8px] text-slate-400 hover:underline cursor-pointer"
                                      >
                                        Primary Only
                                      </button>
                                    </div>
                                  </div>

                                  {/* Photo Thumbnails */}
                                  <div className="flex flex-wrap gap-1.5">
                                    {allProdImages.map((imgUrl, imgIdx) => {
                                      const isSelected = selectedImgs.includes(imgUrl);
                                      return (
                                        <button
                                          key={imgIdx}
                                          type="button"
                                          onClick={() => handleToggleProductImage(prod.id, imgUrl)}
                                          className={`relative rounded-lg overflow-hidden border transition cursor-pointer ${
                                            isSelected
                                              ? 'border-teal-400 ring-2 ring-teal-400/40'
                                              : 'border-white/10 opacity-40 hover:opacity-80'
                                          }`}
                                        >
                                          <img src={imgUrl} alt={`View ${imgIdx + 1}`} className="w-12 h-12 object-cover" />
                                          {isSelected && (
                                            <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-teal-500 text-slate-950 flex items-center justify-center">
                                              <Check size={9} strokeWidth={3} />
                                            </div>
                                          )}
                                          {imgIdx === 0 && (
                                            <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[7px] text-white text-center font-mono">
                                              Primary
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Anti-clumping Toggle */}
                    <label className="flex items-start gap-2 p-2.5 rounded-xl bg-teal-950/20 border border-teal-500/20 cursor-pointer mt-1">
                      <input
                        type="checkbox"
                        checked={templateDraft.sendProductsIndividually !== false}
                        onChange={(e) => setTemplateDraft({ ...templateDraft, sendProductsIndividually: e.target.checked })}
                        className="rounded accent-emerald-500 mt-0.5 cursor-pointer w-3.5 h-3.5"
                      />
                      <div className="flex-1 text-[10px] text-slate-300">
                        <div className="font-bold text-teal-300 flex items-center gap-1">
                          <span>📦 Send Products Individually Without Mixing</span>
                          <span className="text-[8px] px-1 py-0.2 rounded bg-teal-500/20 text-teal-300 font-mono">Anti-Clump</span>
                        </div>
                        <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                          Dispatches each product as its own distinct card with its title, price, description, and selected photos with sequential pacing. Never clumps products into one confusing blob!
                        </div>
                      </div>
                    </label>

                    {/* 🖼️ Pack Media Album / WhatsApp Grid Toggle */}
                    <label className="flex items-start gap-2 p-2.5 rounded-xl bg-teal-950/30 border border-teal-400/30 cursor-pointer mt-2 shadow-sm">
                      <input
                        type="checkbox"
                        checked={templateDraft.packMediaAlbum !== false}
                        onChange={(e) => setTemplateDraft({ ...templateDraft, packMediaAlbum: e.target.checked })}
                        className="rounded accent-teal-400 mt-0.5 cursor-pointer w-3.5 h-3.5"
                      />
                      <div className="flex-1 text-[10px] text-slate-300">
                        <div className="font-bold text-teal-300 flex items-center gap-1">
                          <span>🖼️ Pack Media into WhatsApp Album (Grid)</span>
                          <span className="text-[8px] px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-mono">Clean Album</span>
                        </div>
                        <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                          Packs all product photos together into a native WhatsApp 4-image grid / album without separate captions, and dispatches your text message cleanly underneath.
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })()}

              {/* 🗄️ ATTACH MEDIA ASSETS (MEDIA VAULT & CUSTOM URLS) */}
              {(() => {
                const attachedMediaVaultAssetIds: string[] = Array.isArray(templateDraft.attachedMediaVaultAssetIds)
                  ? templateDraft.attachedMediaVaultAssetIds
                  : [];

                const attachedMediaUrls: string[] = Array.isArray(templateDraft.attachedMediaUrls)
                  ? templateDraft.attachedMediaUrls
                  : (templateDraft.attachedMediaUrl ? [templateDraft.attachedMediaUrl] : []);

                const handleToggleMediaVaultAsset = (asset: MediaVaultAsset) => {
                  const isAttached = attachedMediaVaultAssetIds.includes(asset.id) || attachedMediaUrls.includes(asset.secureUrl);
                  let nextIds: string[];
                  let nextUrls: string[];
                  if (isAttached) {
                    nextIds = attachedMediaVaultAssetIds.filter(id => id !== asset.id);
                    nextUrls = attachedMediaUrls.filter(u => u !== asset.secureUrl);
                  } else {
                    nextIds = [...attachedMediaVaultAssetIds, asset.id];
                    nextUrls = [...attachedMediaUrls, asset.secureUrl];
                  }
                  setTemplateDraft({
                    ...templateDraft,
                    attachedMediaVaultAssetIds: nextIds,
                    attachedMediaUrls: nextUrls,
                    attachedMediaUrl: nextUrls[0] || undefined,
                    attachedMediaType: (asset.mediaType as any) || templateDraft.attachedMediaType || 'image'
                  });
                };

                return (
                  <div className="space-y-2 pt-2 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-300 flex items-center gap-1.5">
                        <Paperclip size={12} className="text-teal-400" />
                        <span>Attach Media Vault Assets & Media URLs</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowMediaVaultBrowser(!showMediaVaultBrowser)}
                        className="text-[9px] px-2 py-0.5 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/30 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <FolderOpen size={10} />
                        <span>{showMediaVaultBrowser ? 'Close Vault ✕' : `Browse Media Vault (${availableMediaVaultAssets.length}) ⚡`}</span>
                      </button>
                    </div>

                    {/* Media Vault Browser Drawer */}
                    {showMediaVaultBrowser && (
                      <div className="p-2.5 rounded-xl bg-slate-900 border border-teal-500/40 space-y-2 animate-fade-in">
                        <div className="flex items-center justify-between gap-2">
                          <div className="relative flex-1">
                            <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Search vault assets by title or tag..."
                              value={mediaVaultSearch}
                              onChange={(e) => setMediaVaultSearch(e.target.value)}
                              className="w-full pl-7 pr-2 py-1 rounded-lg bg-slate-950 border border-slate-800 text-[10px] text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            {(['all', 'image', 'video', 'pdf'] as const).map(t => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setMediaVaultFilterType(t)}
                                className={`text-[8px] uppercase px-1.5 py-0.5 rounded cursor-pointer ${
                                  mediaVaultFilterType === t ? 'bg-teal-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-400'
                                }`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>

                        {availableMediaVaultAssets.length === 0 ? (
                          <div className="p-3 text-center text-[10px] text-slate-400 bg-slate-950/60 rounded-lg border border-dashed border-slate-800">
                            No Media Vault node or assets found on canvas. You can still paste direct media URLs below!
                          </div>
                        ) : (
                          <div className="max-h-40 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-1.5 scrollbar-thin">
                            {availableMediaVaultAssets
                              .filter(a => {
                                if (mediaVaultFilterType !== 'all' && a.mediaType !== mediaVaultFilterType) return false;
                                if (!mediaVaultSearch.trim()) return true;
                                const q = mediaVaultSearch.toLowerCase();
                                return a.title?.toLowerCase().includes(q) || a.tags?.toLowerCase().includes(q);
                              })
                              .map(asset => {
                                const isAttached = attachedMediaVaultAssetIds.includes(asset.id) || attachedMediaUrls.includes(asset.secureUrl);
                                return (
                                  <button
                                    key={asset.id}
                                    type="button"
                                    onClick={() => handleToggleMediaVaultAsset(asset)}
                                    className={`p-1.5 rounded-lg border text-left transition flex items-center gap-1.5 cursor-pointer ${
                                      isAttached
                                        ? 'bg-teal-950/60 border-teal-400 ring-1 ring-teal-400/50'
                                        : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                                    }`}
                                  >
                                    {asset.mediaType === 'image' && asset.secureUrl ? (
                                      <img src={asset.secureUrl} alt={asset.title} className="w-8 h-8 rounded object-cover shrink-0 border border-white/10" />
                                    ) : (
                                      <Paperclip size={14} className="text-teal-400 shrink-0" />
                                    )}
                                    <div className="truncate flex-1 min-w-0">
                                      <div className="text-[10px] font-bold text-white truncate">{asset.title}</div>
                                      <div className="text-[8px] text-slate-400 uppercase font-mono">{asset.mediaType}</div>
                                    </div>
                                    {isAttached && (
                                      <div className="w-3.5 h-3.5 rounded-full bg-teal-500 text-slate-950 flex items-center justify-center shrink-0">
                                        <Check size={9} strokeWidth={3} />
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Attached Media Vault Assets Chips */}
                    {attachedMediaUrls.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {attachedMediaUrls.map((url, uIdx) => (
                          <div key={uIdx} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-teal-950/40 border border-teal-500/30 text-[9px] text-teal-200">
                            <img src={url} alt="Media" className="w-4 h-4 rounded object-cover" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                            <span className="max-w-[140px] truncate font-mono">{url}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const nextUrls = attachedMediaUrls.filter((_, i) => i !== uIdx);
                                setTemplateDraft({
                                  ...templateDraft,
                                  attachedMediaUrls: nextUrls,
                                  attachedMediaUrl: nextUrls[0] || undefined
                                });
                              }}
                              className="text-slate-400 hover:text-rose-400 ml-1 cursor-pointer"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Direct Custom Media URL Input */}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Or paste direct image/video/PDF URL: https://..."
                        value={templateDraft.attachedMediaUrl || ''}
                        onChange={(e) => {
                          const u = e.target.value;
                          setTemplateDraft({
                            ...templateDraft,
                            attachedMediaUrl: u,
                            attachedMediaUrls: u ? [u] : []
                          });
                        }}
                        className="flex-1 bg-slate-900 border border-slate-700 focus:border-teal-400 rounded-xl px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Message Content & Variable Chips */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold text-slate-300 flex items-center gap-1">
                    <MessageSquare size={11} className="text-teal-400" />
                    <span>WhatsApp Message Text</span>
                  </label>
                  <span className="text-[9px] text-slate-400">Supports *bold*, _italic_, emoji</span>
                </div>

                {/* Variable helper buttons */}
                <div className="flex flex-wrap items-center gap-1 mb-1.5">
                  <span className="text-[9px] text-slate-500">Insert:</span>
                  {[
                    { tag: '{{store_name}}', label: 'Store Name' },
                    { tag: '{{customer_name}}', label: 'Customer Name' },
                    { tag: '{{product_name}}', label: 'Product Name' },
                    { tag: '{{price}}', label: 'Price' },
                    { tag: '{{currency}}', label: 'Currency' }
                  ].map((v) => (
                    <button
                      key={v.tag}
                      type="button"
                      onClick={() => {
                        const current = templateDraft.messageText || '';
                        setTemplateDraft({ ...templateDraft, messageText: current + (current ? ' ' : '') + v.tag });
                      }}
                      className="text-[9px] font-mono px-2 py-0.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 cursor-pointer"
                    >
                      +{v.label}
                    </button>
                  ))}
                </div>

                <textarea
                  rows={4}
                  value={templateDraft.messageText || ''}
                  onChange={(e) => setTemplateDraft({ ...templateDraft, messageText: e.target.value })}
                  placeholder="👋 Hello {{customer_name}}! Welcome to {{store_name}}. How can we assist you today?"
                  className="w-full bg-slate-900 border border-slate-700 focus:border-teal-400 rounded-xl p-2.5 text-xs text-white leading-relaxed focus:outline-none resize-y"
                />
              </div>

              {/* Live WhatsApp Simulation Bubble */}
              {(() => {
                const rawProductIds: string[] = Array.isArray(templateDraft.attachedProductIds) && templateDraft.attachedProductIds.length > 0
                  ? templateDraft.attachedProductIds
                  : (templateDraft.attachedProductId ? [templateDraft.attachedProductId] : []);

                const attachedMediaUrls: string[] = Array.isArray(templateDraft.attachedMediaUrls)
                  ? templateDraft.attachedMediaUrls
                  : (templateDraft.attachedMediaUrl ? [templateDraft.attachedMediaUrl] : []);

                return (
                  <div className="p-3 rounded-2xl bg-[#0b141a] border border-[#1f2c34] text-slate-200 font-sans text-xs space-y-2.5">
                    <div className="text-[9px] uppercase font-mono tracking-wider text-teal-400 font-bold flex items-center justify-between">
                      <span>📱 Customer WhatsApp View (Simulation)</span>
                      <span className="text-[8px] text-emerald-400 flex items-center gap-1 font-mono">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        <span>0 LLM Tokens Dispatched</span>
                      </span>
                    </div>

                    {/* Bubble 1: Main Greeting Text */}
                    <div className="p-2.5 rounded-xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-1">
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {(() => {
                          const selProds = products.filter(p => rawProductIds.includes(p.id));
                          const raw = templateDraft.messageText || '👋 Type message content above to preview...';
                          return raw
                            .replace(/\{\{\s*store_name\s*\}\}/gi, 'Blossom Lifestyle Store')
                            .replace(/\{\{\s*customer_name\s*\}\}/gi, 'Kamal')
                            .replace(/\{\{\s*product_name\s*\}\}/gi, selProds.length > 0 ? selProds.map(p => p.name).join(', ') : 'Premium Denim Jacket')
                            .replace(/\{\{\s*price\s*\}\}/gi, selProds[0] ? `${selProds[0].currency || defaultCurrency || 'LKR'} ${selProds[0].price?.toLocaleString() || ''}` : 'LKR 4,500')
                            .replace(/\{\{\s*currency\s*\}\}/gi, selProds[0]?.currency || defaultCurrency || 'LKR');
                        })()}
                      </div>
                    </div>

                    {/* Attached Media Vault Assets Bubble */}
                    {attachedMediaUrls.length > 0 && (
                      <div className="p-2 rounded-xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-1">
                        <div className="text-[9px] text-teal-300 font-mono">📎 Attached Media ({attachedMediaUrls.length}):</div>
                        <div className="flex gap-1.5 overflow-x-auto py-1">
                          {attachedMediaUrls.map((url, i) => (
                            <img key={i} src={url} alt={`Media ${i+1}`} className="w-16 h-16 object-cover rounded-lg border border-white/10 shrink-0" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Standalone Product Cards (Anti-Clumped) */}
                    {rawProductIds.map((pid, idx) => {
                      const p = products.find(x => x.id === pid);
                      if (!p) return null;
                      const selectedImgs = templateDraft.selectedProductImages?.[p.id] || (p.imageUrl ? [p.imageUrl] : []);
                      const curr = p.currency || defaultCurrency || 'LKR';
                      return (
                        <div key={pid} className="p-2.5 rounded-xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-2 border-l-2 border-teal-400">
                          <div className="text-[9px] font-mono text-teal-300 flex items-center justify-between">
                            <span>Card #{idx + 1}: Standalone Product Message</span>
                            <span className="text-[8px] text-slate-400">{selectedImgs.length} photo{selectedImgs.length === 1 ? '' : 's'}</span>
                          </div>
                          {selectedImgs.length > 0 && (
                            <div className="rounded-lg overflow-hidden border border-white/10 bg-black/40">
                              <img src={selectedImgs[0]} alt={p.name} className="w-full h-28 object-cover" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                            </div>
                          )}
                          {selectedImgs.length > 1 && (
                            <div className="flex gap-1 overflow-x-auto py-0.5">
                              {selectedImgs.slice(1).map((img, i) => (
                                <img key={i} src={img} alt={`View ${i+2}`} className="w-10 h-10 object-cover rounded border border-white/10 shrink-0" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                              ))}
                            </div>
                          )}
                          <div className="text-[11px] font-sans leading-relaxed space-y-0.5">
                            <div className="font-bold text-white">🛍️ {p.name}</div>
                            <div className="text-teal-300 font-bold">💵 Price: {curr} {p.price?.toLocaleString()}</div>
                            {p.category && <div className="text-slate-300 text-[10px]">📦 Category: {p.category}</div>}
                            {p.description && <div className="text-slate-400 text-[9px] line-clamp-2 mt-1">{p.description}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                <button
                  type="button"
                  onClick={() => { setEditingTemplateId(null); setTemplateDraft({}); }}
                  className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!templateDraft.title?.trim()) {
                      alert('Please provide a template title');
                      return;
                    }
                    if (!templateDraft.messageText?.trim()) {
                      alert('Please provide message text');
                      return;
                    }

                    const rawProductIds: string[] = Array.isArray(templateDraft.attachedProductIds) && templateDraft.attachedProductIds.length > 0
                      ? templateDraft.attachedProductIds
                      : (templateDraft.attachedProductId ? [templateDraft.attachedProductId] : []);

                    const newTmpl: PreFilledMessageTemplate = {
                      id: templateDraft.id || 'tmpl_' + Date.now(),
                      title: templateDraft.title.trim(),
                      triggerType: templateDraft.triggerType || 'keyword_match',
                      triggerKeywords: Array.isArray(templateDraft.triggerKeywords) && templateDraft.triggerKeywords.length > 0
                        ? templateDraft.triggerKeywords
                        : keywordInputDraft.split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
                      messageText: templateDraft.messageText.trim(),
                      attachedProductId: rawProductIds[0] || undefined,
                      attachedProductIds: rawProductIds.length > 0 ? rawProductIds : undefined,
                      attachedMediaUrl: templateDraft.attachedMediaUrl?.trim() || undefined,
                      attachedMediaUrls: Array.isArray(templateDraft.attachedMediaUrls) && templateDraft.attachedMediaUrls.length > 0 ? templateDraft.attachedMediaUrls : undefined,
                      attachedMediaType: templateDraft.attachedMediaType || 'image',
                      attachedMediaVaultAssetIds: Array.isArray(templateDraft.attachedMediaVaultAssetIds) && templateDraft.attachedMediaVaultAssetIds.length > 0 ? templateDraft.attachedMediaVaultAssetIds : undefined,
                      selectedProductImages: templateDraft.selectedProductImages || undefined,
                      sendProductsIndividually: templateDraft.sendProductsIndividually !== false,
                      isEnabled: templateDraft.isEnabled !== false,
                      priority: templateDraft.priority || 10,
                    };

                    let updatedList: PreFilledMessageTemplate[];
                    if (editingTemplateId === 'new') {
                      updatedList = [...preFilledTemplates, newTmpl];
                    } else {
                      updatedList = preFilledTemplates.map(t => t.id === editingTemplateId ? newTmpl : t);
                    }
                    setPreFilledTemplates(updatedList);
                    setEditingTemplateId(null);
                    setTemplateDraft({});
                    persistNodeDataDirectly({ preFilledTemplates: updatedList });
                  }}
                  className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs shadow-md shadow-emerald-500/20 cursor-pointer flex items-center gap-1.5"
                >
                  <Check size={13} />
                  <span>Save Template</span>
                </button>
              </div>
            </div>
          ) : (
            /* Template Cards List */
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-300">
                  Pre-filled Templates ({preFilledTemplates.length})
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setEditingTemplateId('new');
                    setTemplateDraft({
                      id: 'tmpl_' + Date.now(),
                      title: '',
                      triggerType: 'keyword_match',
                      triggerKeywords: [],
                      messageText: '',
                      packMediaAlbum: true,
                      isEnabled: true,
                    });
                    setKeywordInputDraft('');
                  }}
                  className="px-2.5 py-1 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/30 text-[10px] font-bold flex items-center gap-1 cursor-pointer"
                >
                  <Plus size={11} />
                  <span>New Template</span>
                </button>
              </div>

              {preFilledTemplates.length === 0 ? (
                <div className="p-4 rounded-xl bg-slate-900/40 border border-dashed border-slate-800 text-center space-y-2">
                  <p className="text-[11px] text-slate-400">No pre-filled templates configured yet.</p>
                  <button
                    type="button"
                    onClick={() => {
                      const sample = DEFAULT_PRE_FILLED_TEMPLATES;
                      setPreFilledTemplates(sample);
                      persistNodeDataDirectly({ preFilledTemplates: sample });
                    }}
                    className="text-[10px] px-3 py-1 rounded-lg bg-teal-500/20 text-teal-300 border border-teal-500/30 cursor-pointer"
                  >
                    Load Default Welcome Greeting
                  </button>
                </div>
              ) : (
                preFilledTemplates.map((tmpl) => (
                  <div
                    key={tmpl.id}
                    className={`p-3 rounded-xl border transition ${
                      tmpl.isEnabled
                        ? 'bg-slate-900/80 border-slate-700/70 hover:border-teal-500/40'
                        : 'bg-slate-950/60 border-slate-800 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="checkbox"
                            checked={tmpl.isEnabled}
                            onChange={(e) => {
                              const updated = preFilledTemplates.map(t => t.id === tmpl.id ? { ...t, isEnabled: e.target.checked } : t);
                              setPreFilledTemplates(updated);
                              persistNodeDataDirectly({ preFilledTemplates: updated });
                            }}
                            className="rounded accent-emerald-500 cursor-pointer w-3.5 h-3.5"
                          />
                          <span className="font-bold text-white text-[11px]">{tmpl.title}</span>
                          {tmpl.isEnabled ? (
                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold flex items-center gap-1">
                              <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></span>
                              <span>Active</span>
                            </span>
                          ) : (
                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30 font-bold flex items-center gap-1">
                              <span className="w-1 h-1 rounded-full bg-rose-400"></span>
                              <span>Paused</span>
                            </span>
                          )}
                          {tmpl.triggerType === 'first_contact_greeting' && (
                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
                              👋 Greeting
                            </span>
                          )}
                          {tmpl.triggerType === 'keyword_match' && (
                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                              🔍 Keywords
                            </span>
                          )}
                          {tmpl.triggerType === 'product_inquiry' && (
                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                              🛍️ Product
                            </span>
                          )}
                        </div>

                        {/* Keywords badge */}
                        {(tmpl.triggerKeywords || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[8px] text-slate-500">Triggers on:</span>
                            {tmpl.triggerKeywords.slice(0, 5).map((kw, i) => (
                              <span key={i} className="text-[8px] font-mono px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-white/5">
                                {kw}
                              </span>
                            ))}
                            {tmpl.triggerKeywords.length > 5 && (
                              <span className="text-[8px] text-slate-500">+{tmpl.triggerKeywords.length - 5} more</span>
                            )}
                          </div>
                        )}

                        {/* Attached product or media pill */}
                        <div className="flex items-center gap-2 flex-wrap text-[9px] text-slate-400">
                          {(() => {
                            const rawIds = Array.isArray(tmpl.attachedProductIds) && tmpl.attachedProductIds.length > 0
                              ? tmpl.attachedProductIds
                              : (tmpl.attachedProductId ? [tmpl.attachedProductId] : []);
                            if (rawIds.length === 0) return null;
                            return (
                              <span className="text-emerald-400 flex items-center gap-1 font-medium">
                                <Package size={10} />
                                <span>
                                  {rawIds.length === 1
                                    ? products.find(p => p.id === rawIds[0])?.name || '1 Product'
                                    : `${rawIds.length} Products`}
                                </span>
                              </span>
                            );
                          })()}
                          {((tmpl.attachedMediaUrls && tmpl.attachedMediaUrls.length > 0) || tmpl.attachedMediaUrl) && (
                            <span className="text-teal-400 flex items-center gap-1">
                              <Paperclip size={10} />
                              <span>
                                Media ({Array.isArray(tmpl.attachedMediaUrls) && tmpl.attachedMediaUrls.length > 1
                                  ? `${tmpl.attachedMediaUrls.length} Assets`
                                  : (tmpl.attachedMediaType || 'Asset')})
                              </span>
                            </span>
                          )}
                        </div>

                        {/* Message snippet */}
                        <p className="text-[10px] text-slate-300 line-clamp-2 leading-relaxed">
                          {tmpl.messageText}
                        </p>
                      </div>

                      {/* Card Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingTemplateId(tmpl.id);
                            setTemplateDraft(tmpl);
                            setKeywordInputDraft((tmpl.triggerKeywords || []).join(', '));
                          }}
                          className="p-1 rounded bg-slate-800 hover:bg-teal-500/20 hover:text-teal-300 text-slate-400 transition cursor-pointer"
                          title="Edit Template"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const clone: PreFilledMessageTemplate = {
                              ...tmpl,
                              id: 'tmpl_' + Date.now(),
                              title: `${tmpl.title} (Copy)`,
                            };
                            const updated = [...preFilledTemplates, clone];
                            setPreFilledTemplates(updated);
                            persistNodeDataDirectly({ preFilledTemplates: updated });
                          }}
                          className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 transition cursor-pointer"
                          title="Duplicate"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Delete template "${tmpl.title}"?`)) {
                              const updated = preFilledTemplates.filter(t => t.id !== tmpl.id);
                              setPreFilledTemplates(updated);
                              persistNodeDataDirectly({ preFilledTemplates: updated });
                            }
                          }}
                          className="p-1 rounded bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 text-slate-400 transition cursor-pointer"
                          title="Delete Template"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Subheader Controls / Filters ─────────────────────────────── */}
      <div className="px-4 py-2.5 bg-slate-900/60 border-b border-slate-800/60 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search catalog items, keywords, or SKU..."
            className="w-full pl-7 pr-2 py-1 bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl text-[11px] text-white placeholder-slate-500 focus:outline-none transition"
          />
        </div>

        {categories.length > 0 && (
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-950 border border-slate-800 focus:border-emerald-500 text-[10px] font-mono text-slate-300 rounded-xl px-2 py-1 focus:outline-none"
          >
            <option value="all">All Categories</option>
            {categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}

        {/* Recycle Bin Button */}
        <button
          type="button"
          onClick={() => {
            setShowRecycleBin(!showRecycleBin);
            setShowSnapshots(false);
            setIsAddingProduct(false);
          }}
          className={`px-2 py-1 rounded-xl border text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer ${
            showRecycleBin 
              ? 'bg-rose-500/25 text-rose-300 border-rose-500/50 shadow-md shadow-rose-500/20' 
              : rawRecycleBin.length > 0 
                ? 'bg-rose-950/40 text-rose-400 border-rose-800/60 hover:bg-rose-900/40' 
                : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-slate-200'
          }`}
          title="Recycle Bin (Deleted Products Recovery)"
        >
          <Trash2 size={12} className={rawRecycleBin.length > 0 ? 'text-rose-400' : ''} />
          {rawRecycleBin.length > 0 && (
            <span className="text-[9px] px-1 py-0.2 rounded-full bg-rose-500/30 text-rose-300 font-mono font-bold">
              {rawRecycleBin.length}
            </span>
          )}
        </button>

        {/* Snapshots Button */}
        <button
          type="button"
          onClick={() => {
            setShowSnapshots(!showSnapshots);
            setShowRecycleBin(false);
            setIsAddingProduct(false);
          }}
          className={`px-2 py-1 rounded-xl border text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer ${
            showSnapshots 
              ? 'bg-indigo-500/25 text-indigo-300 border-indigo-500/50 shadow-md shadow-indigo-500/20' 
              : rawSnapshots.length > 0
                ? 'bg-indigo-950/40 text-indigo-300 border-indigo-800/60 hover:bg-indigo-900/40'
                : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-slate-200'
          }`}
          title="Catalog Snapshots & Backups (1-Click Rollback)"
        >
          <History size={12} className={rawSnapshots.length > 0 ? 'text-indigo-400' : ''} />
          {rawSnapshots.length > 0 && (
            <span className="text-[9px] px-1 py-0.2 rounded-full bg-indigo-500/30 text-indigo-300 font-mono font-bold">
              {rawSnapshots.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            const nextState = !isAddingProduct;
            setIsAddingProduct(nextState);
            if (nextState && selectedCategory !== 'all') {
              setNewProdCategory(selectedCategory);
            }
            setShowRecycleBin(false);
            setShowSnapshots(false);
          }}
          className="p-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition flex items-center justify-center cursor-pointer shadow-lg shadow-emerald-500/20"
          title="Add New Product"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* ── Category Bar & Navigation ────────────────────────────────── */}
      <div className="px-4 py-2 bg-slate-950/90 border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-1.5 text-[11px]">
        <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 scrollbar-none flex-1 max-w-full">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-2.5 py-1 rounded-lg font-mono text-[10px] font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/30 font-black'
                : 'bg-slate-900/90 text-slate-300 hover:text-white border border-slate-800'
            }`}
          >
            <span>All Items</span>
            <span className={`px-1 py-0.2 rounded text-[9px] ${
              selectedCategory === 'all' ? 'bg-slate-950/20 text-slate-950' : 'bg-slate-800 text-slate-400'
            }`}>
              {products.length}
            </span>
          </button>

          {categories.map((cat) => {
            const catCount = products.filter(p => getProductCategories(p).some(c => c.toLowerCase() === cat.toLowerCase())).length;
            const isCatActive = selectedCategory.toLowerCase() === cat.toLowerCase();
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-2.5 py-1 rounded-lg font-mono text-[10px] font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer ${
                  isCatActive
                    ? 'bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/30 font-black'
                    : 'bg-slate-900/90 text-slate-300 hover:text-white border border-slate-800'
                }`}
              >
                <span>📁 {cat}</span>
                <span className={`px-1 py-0.2 rounded text-[9px] ${
                  isCatActive ? 'bg-slate-950/20 text-slate-950' : 'bg-slate-800 text-slate-400'
                }`}>
                  {catCount}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setShowCategoryManager(!showCategoryManager)}
            className="px-2 py-1 rounded-lg bg-emerald-950/60 hover:bg-emerald-900/60 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono font-bold flex items-center gap-1 shrink-0 cursor-pointer transition active:scale-95"
            title="Manage Categories (Create, Rename, Delete)"
          >
            <FolderPlus size={11} />
            <span>+ Category</span>
          </button>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setViewMode(viewMode === 'flat' ? 'grouped' : 'flat')}
            className={`px-2 py-1 rounded-lg text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer ${
              viewMode === 'grouped'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
            }`}
            title="Toggle Grouped by Category vs Flat List View"
          >
            <Layers size={11} />
            <span>{viewMode === 'grouped' ? 'Grouped' : 'List'}</span>
          </button>
        </div>
      </div>

      {/* ── Category Management Drawer ───────────────────────────────── */}
      {showCategoryManager && (
        <div className="p-3 bg-slate-950/95 border-b border-emerald-500/40 space-y-2.5 animate-fade-in">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-800">
            <span className="text-[10px] uppercase font-mono font-bold text-emerald-400 flex items-center gap-1.5">
              <FolderPlus size={12} /> Product Categories Management
            </span>
            <button
              type="button"
              onClick={() => setShowCategoryManager(false)}
              className="text-slate-400 hover:text-white text-xs cursor-pointer"
            >
              ✕ Close
            </button>
          </div>

          {/* Tab Switcher: Active Categories vs Category Recycle Bin */}
          <div className="flex items-center gap-1.5 pb-1 border-b border-slate-800">
            <button
              type="button"
              onClick={() => setCategoryTab('active')}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold transition cursor-pointer flex items-center gap-1 ${
                categoryTab === 'active'
                  ? 'bg-emerald-500 text-slate-950 font-black shadow-sm'
                  : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
              }`}
            >
              <Folder size={11} />
              <span>Active ({categories.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setCategoryTab('recycle')}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold transition cursor-pointer flex items-center gap-1 ${
                categoryTab === 'recycle'
                  ? 'bg-rose-500 text-white font-black shadow-sm'
                  : 'bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              <Trash2 size={11} className={rawCategoryRecycle.length > 0 ? 'text-rose-400' : ''} />
              <span>Recycle Bin ({rawCategoryRecycle.length})</span>
            </button>
          </div>

          {categoryTab === 'active' ? (
            <>
              <div className="p-2.5 bg-slate-900/80 rounded-xl border border-emerald-500/30 space-y-2">
                <div className="text-[10px] font-bold text-emerald-400 flex items-center gap-1">
                  <FolderPlus size={11} />
                  <span>Add New Category</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="Category name (e.g. Mini Car, Vans, Bouquets)*"
                    className="flex-1 bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1 text-xs text-white placeholder-slate-500 focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddCategory(newCategoryInput, newCategoryRuleInput);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddCategory(newCategoryInput, newCategoryRuleInput)}
                    className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs rounded-lg transition shrink-0 cursor-pointer"
                  >
                    + Create
                  </button>
                </div>
                <div>
                  <input
                    type="text"
                    value={newCategoryRuleInput}
                    onChange={(e) => setNewCategoryRuleInput(e.target.value)}
                    placeholder="Category Description & Pricing Rules (e.g. 1 km extra Rs 40, refundable deposit Rs. 15,000, 5% fleet discount)"
                    className="w-full bg-slate-950 border border-cyan-500/30 focus:border-cyan-400 rounded-lg px-2.5 py-1 text-xs text-cyan-200 placeholder-slate-500 focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddCategory(newCategoryInput, newCategoryRuleInput);
                      }
                    }}
                  />
                  <span className="text-[9px] text-cyan-400/80 mt-0.5 block">
                    💡 Luna AI uses this category rule/description when advising customers and calculating quotes.
                  </span>
                </div>
              </div>

              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-800">
                {categories.map((cat) => {
                  const count = products.filter(p => getProductCategories(p).some(c => c.toLowerCase() === cat.toLowerCase())).length;
                  const isEditingThis = renamingCategory?.oldName === cat;
                  const isEditingThisRule = editingCategoryRule?.catName === cat;
                  const currentRule = categoryRules[cat] || '';

                  return (
                    <div key={cat} className="p-2 bg-slate-900/70 rounded-lg border border-slate-800 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        {isEditingThis ? (
                          <div className="flex items-center gap-1.5 flex-1 mr-2">
                            <input
                              type="text"
                              value={renamingCategory.newName}
                              onChange={(e) => setRenamingCategory({ ...renamingCategory, newName: e.target.value })}
                              className="bg-slate-950 border border-emerald-400 rounded px-1.5 py-0.5 text-xs text-white flex-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleRenameCategory(renamingCategory.oldName, renamingCategory.newName);
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRenameCategory(renamingCategory.oldName, renamingCategory.newName)}
                              className="text-[10px] font-bold text-emerald-400 hover:text-emerald-300 px-1.5 py-0.5 bg-emerald-950 rounded border border-emerald-500/40 cursor-pointer"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setRenamingCategory(null)}
                              className="text-[10px] text-slate-400 hover:text-white px-1 cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-slate-200">📁 {cat}</span>
                            <span className="text-[9px] font-mono text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded">
                              {count} items
                            </span>
                          </div>
                        )}

                        {!isEditingThis && (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setRenamingCategory({ oldName: cat, newName: cat })}
                              className="p-1 text-slate-400 hover:text-emerald-400 rounded transition cursor-pointer"
                              title="Rename Category"
                            >
                              <Edit2 size={11} />
                            </button>
                            {cat.toLowerCase() !== 'general' && (
                              <button
                                type="button"
                                onClick={() => handleDeleteCategory(cat)}
                                className="p-1 text-slate-400 hover:text-rose-400 rounded transition cursor-pointer"
                                title="Delete Category"
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Category Description & Rules Section */}
                      {isEditingThisRule ? (
                        <div className="p-1.5 bg-slate-950 rounded border border-cyan-500/50 space-y-1">
                          <div className="text-[9px] text-cyan-400 font-semibold flex items-center gap-1">
                            <FileText size={10} />
                            <span>Description / Rules for "{cat}"</span>
                          </div>
                          <textarea
                            rows={2}
                            value={editingCategoryRule.rule}
                            onChange={(e) => setEditingCategoryRule({ ...editingCategoryRule, rule: e.target.value })}
                            className="w-full bg-slate-900 border border-cyan-500/40 rounded p-1 text-[11px] text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none"
                            placeholder="e.g. 1 km extra charge Rs 40, refundable deposit Rs. 15,000..."
                            autoFocus
                          />
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleSaveCategoryRule(editingCategoryRule.catName, editingCategoryRule.rule)}
                              className="text-[10px] font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 px-2 py-0.5 rounded cursor-pointer"
                            >
                              Save Rule
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCategoryRule(null)}
                              className="text-[10px] text-slate-400 hover:text-white px-1.5 py-0.5 cursor-pointer"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-1 bg-slate-950/80 rounded border border-slate-800/80 flex items-start justify-between gap-1.5">
                          <div className="flex items-start gap-1 flex-1 min-w-0">
                            <FileText size={11} className={currentRule ? "text-cyan-400 shrink-0 mt-0.5" : "text-slate-600 shrink-0 mt-0.5"} />
                            {currentRule ? (
                              <p className="text-[10px] text-cyan-300/90 leading-tight break-words">{currentRule}</p>
                            ) : (
                              <span className="text-[9px] text-slate-500 italic">No description or rules defined.</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditingCategoryRule({ catName: cat, rule: currentRule })}
                            className="text-[9px] font-medium text-cyan-400 hover:underline px-1 py-0.2 bg-cyan-950/40 rounded border border-cyan-500/20 shrink-0 cursor-pointer"
                          >
                            {currentRule ? 'Edit' : '+ Add'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            /* ── Category Recycle Bin Tab ── */
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-rose-950">
              {rawCategoryRecycle.length === 0 ? (
                <div className="py-6 text-center text-slate-500 text-xs font-mono">
                  <Trash2 size={18} className="mx-auto mb-1.5 text-slate-600 opacity-60" />
                  <span>Category Recycle Bin is empty. No deleted categories.</span>
                </div>
              ) : (
                rawCategoryRecycle.map((recItem) => (
                  <div
                    key={recItem.id}
                    className="p-2 rounded-xl bg-slate-900/80 border border-rose-500/30 flex items-center justify-between gap-2.5 hover:border-rose-500/50 transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Folder size={12} className="text-rose-400 shrink-0" />
                        <span className="font-mono font-bold text-white text-xs truncate">
                          📁 {recItem.name}
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-400 font-mono mt-0.5 flex items-center gap-2">
                        <span>{recItem.productCount || 0} items re-routed</span>
                        <span>•</span>
                        <span>{recItem.deletedAt ? new Date(recItem.deletedAt).toLocaleDateString() : 'Recent'}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRestoreCategory(recItem.id)}
                      className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[10px] font-mono font-bold rounded-lg transition flex items-center gap-1 cursor-pointer shrink-0 active:scale-95"
                      title={`Restore category "${recItem.name}"`}
                    >
                      <RotateCcw size={11} />
                      <span>Restore</span>
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Datalist for Category Auto-Complete */}
      <datalist id="catalog-categories-list">
        {categories.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {/* ── Add Product Inline Drawer Form ───────────────────────────── */}
      {isAddingProduct && (
        <form onSubmit={handleCreateProduct} className="p-3.5 bg-emerald-950/20 border-b border-emerald-500/30 space-y-2.5 animate-fade-in">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-mono font-bold text-emerald-400 flex items-center gap-1">
              <Sparkles size={11} /> New E-Commerce Item
            </span>
            <button
              type="button"
              onClick={() => setIsAddingProduct(false)}
              className="text-slate-400 hover:text-slate-200 text-xs"
            >
              Cancel
            </button>
          </div>

          <input
            type="text"
            required
            value={newProdName}
            onChange={(e) => setNewProdName(e.target.value)}
            placeholder="Product Name (e.g. 🌹 Red Rose Bouquet)"
            className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
          />

          {/* Multi-Category Selector (Tag Cloud + Quick Add) */}
          <div className="space-y-1.5 p-2 rounded-xl bg-slate-950/80 border border-emerald-500/30">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-mono font-bold text-emerald-300 uppercase flex items-center gap-1.5">
                <Folder size={11} className="text-emerald-400" />
                <span>Assigned Categories (Multiple Allowed)</span>
              </label>
              <span className="text-[9px] font-mono text-slate-400">
                {newProdCategories.length} selected
              </span>
            </div>

            {/* Selected category chips */}
            <div className="flex flex-wrap gap-1.5 min-h-[30px] p-1.5 rounded-lg bg-slate-950 border border-emerald-500/20 items-center">
              {newProdCategories.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                >
                  <Folder size={9} className="text-emerald-400" />
                  <span>{cat}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const remaining = newProdCategories.filter((c) => c !== cat);
                      setNewProdCategories(remaining.length > 0 ? remaining : ['General']);
                      setNewProdCategory(remaining[0] || 'General');
                    }}
                    className="text-emerald-400 hover:text-rose-400 ml-1 cursor-pointer font-black text-xs"
                    title="Remove category"
                  >
                    ×
                  </button>
                </span>
              ))}

              {/* Inline Input to add/create category */}
              <input
                type="text"
                list="catalog-categories-list"
                value={newProdCategoryTagInput}
                onChange={(e) => setNewProdCategoryTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = newProdCategoryTagInput.trim();
                    if (val && !newProdCategories.includes(val)) {
                      setNewProdCategories([...newProdCategories.filter((c) => c !== 'General'), val]);
                      setNewProdCategory(val);
                      setNewProdCategoryTagInput('');
                    }
                  }
                }}
                placeholder="+ Add or type category..."
                className="bg-transparent border-none text-[10px] text-white placeholder-slate-500 focus:outline-none font-mono flex-1 min-w-[120px] px-1"
              />
            </div>

            {/* Quick-add available store categories pill bar */}
            {categories.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap pt-0.5">
                <span className="text-[9px] font-mono text-slate-500 uppercase">Quick Add:</span>
                {categories
                  .filter((c) => !newProdCategories.includes(c))
                  .slice(0, 8)
                  .map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        setNewProdCategories([...newProdCategories.filter((c) => c !== 'General'), cat]);
                        setNewProdCategory(cat);
                      }}
                      className="text-[9px] font-mono text-slate-300 hover:text-emerald-300 bg-slate-900 hover:bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/20 hover:border-emerald-500/40 transition cursor-pointer"
                    >
                      + {cat}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2">
            <input
              type="number"
              step="any"
              required
              value={newProdPrice}
              onChange={(e) => setNewProdPrice(e.target.value)}
              placeholder={`Price (${defaultCurrency}) *`}
              className="bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
            />
            <input
              type="number"
              step="any"
              value={newProdOriginalPrice}
              onChange={(e) => setNewProdOriginalPrice(e.target.value)}
              placeholder={`Original Price`}
              className="bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2 py-1 text-xs text-slate-400 placeholder-slate-600 focus:outline-none font-mono"
            />
            <input
              type="number"
              value={newProdStockQty}
              onChange={(e) => setNewProdStockQty(e.target.value)}
              placeholder="Stock Units"
              className="bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2 py-1 text-xs text-emerald-300 placeholder-slate-600 focus:outline-none font-mono"
            />
            <input
              type="text"
              value={newProdUnit}
              onChange={(e) => setNewProdUnit(e.target.value)}
              placeholder="Unit (Pieces)"
              className="bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none"
            />
          </div>

          <textarea
            rows={2}
            value={newProdDesc}
            onChange={(e) => setNewProdDesc(e.target.value)}
            placeholder="Description & specs (helps Luna describe the product naturally to customers)"
            className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2.5 py-1 text-xs text-white placeholder-slate-500 focus:outline-none resize-none"
          />

          {/* ── Multi-Media, Document Upload & Luna AI Ingestion ──────── */}
          <div className="p-2.5 rounded-2xl bg-slate-950/70 border border-emerald-500/30 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-mono font-bold text-emerald-300 flex items-center gap-1">
                <Paperclip size={11} /> Multi-Media & Documents ({newProdMedia.length})
              </span>
              <button
                type="button"
                onClick={() => handleAiImproveProduct(false)}
                disabled={isAiEnriching}
                className="px-2.5 py-1 rounded-xl bg-gradient-to-r from-emerald-500/30 to-teal-500/30 hover:from-emerald-500/40 hover:to-teal-500/40 border border-emerald-400/60 text-emerald-200 hover:text-white font-bold text-[10px] flex items-center gap-1.5 transition shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50 cursor-pointer"
                title="Luna AI analyzes uploaded images, videos & documents to auto-enrich description, category, and keywords"
              >
                {isAiEnriching ? (
                  <>
                    <Loader2 size={11} className="animate-spin text-emerald-400" />
                    <span>Luna Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Wand2 size={11} className="text-emerald-300 animate-pulse" />
                    <span>✨ Improve with Luna AI</span>
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={newProdMediaUrl}
                onChange={(e) => setNewProdMediaUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newProdMediaUrl.trim()) {
                    e.preventDefault();
                    const url = newProdMediaUrl.trim();
                    const ext = url.split('.').pop()?.toLowerCase() || '';
                    let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
                    if (['mp4', 'mov', 'webm'].includes(ext)) mType = 'video';
                    else if (['pdf'].includes(ext)) mType = 'pdf';
                    else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';
                    setNewProdMedia(prev => [...prev, {
                      id: `med_${Date.now()}`,
                      type: mType,
                      url,
                      name: `Asset ${prev.length + 1}`
                    }]);
                    setNewProdMediaUrl('');
                  }
                }}
                placeholder="Paste URL + Enter, or select multiple files →"
                className="flex-1 bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
              />
              <input
                type="file"
                ref={newFileInputRef}
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleMultiFileUpload(e.target.files, false);
                  }
                }}
                accept="image/*,video/*,application/pdf,.doc,.docx,.txt"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => newFileInputRef.current?.click()}
                disabled={isUploadingMedia}
                className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-xl text-[10px] font-bold font-mono flex items-center gap-1 transition cursor-pointer shrink-0 disabled:opacity-50 shadow-sm"
                title="Upload photos, videos, or spec sheets (multiple supported)"
              >
                {isUploadingMedia ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Upload size={11} />
                )}
                <span>+ Upload</span>
              </button>
            </div>

            {/* Attached Media Chips */}
            {newProdMedia.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1 max-h-24 overflow-y-auto pr-1">
                {newProdMedia.map((m, idx) => (
                  <div
                    key={m.id || idx}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/40 text-[10px] font-mono text-emerald-200"
                  >
                    {m.type === 'image' && (
                      <img
                        src={m.url}
                        alt=""
                        className="w-4 h-4 rounded object-cover border border-emerald-400/40"
                        onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                      />
                    )}
                    {m.type === 'video' && <Film size={11} className="text-purple-400" />}
                    {m.type === 'pdf' && <FileText size={11} className="text-rose-400" />}
                    {m.type === 'document' && <FileText size={11} className="text-amber-400" />}
                    <span className="truncate max-w-[100px]" title={m.name || m.url}>
                      {m.name || m.type.toUpperCase()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedPreviewMedia({ title: m.name || 'Preview', url: m.url, type: m.type })}
                      className="text-slate-400 hover:text-emerald-300 ml-0.5"
                      title="Preview Media"
                    >
                      <Eye size={10} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewProdMedia(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-rose-400 ml-0.5"
                      title="Remove"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Optional Raw Text / ChatGPT Paste */}
            <div className="pt-1.5 border-t border-emerald-500/20">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                  <FileText size={10} className="text-emerald-400" />
                  <span>Paste ChatGPT / Raw Specs (Optional)</span>
                </span>
                {newProdRawText && (
                  <button
                    type="button"
                    onClick={() => setNewProdRawText('')}
                    className="text-[9px] text-slate-500 hover:text-rose-400 cursor-pointer transition"
                  >
                    Clear
                  </button>
                )}
              </div>
              <textarea
                rows={2}
                value={newProdRawText}
                onChange={(e) => setNewProdRawText(e.target.value)}
                placeholder="Paste ChatGPT output, full descriptions, raw supplier specs, or price lists here... Luna will parse everything!"
                className="w-full bg-slate-900/90 border border-emerald-500/20 focus:border-emerald-400/70 rounded-xl px-2.5 py-1 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none resize-none font-mono"
              />
            </div>
          </div>

          <input
            type="text"
            value={newProdKeywords}
            onChange={(e) => setNewProdKeywords(e.target.value)}
            placeholder="Search Keywords (comma separated, auto-enriched by Luna AI)"
            className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-xl px-2.5 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
          />

          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 font-mono cursor-pointer">
              <input
                type="checkbox"
                checked={newProdAvailable}
                onChange={(e) => setNewProdAvailable(e.target.checked)}
                className="w-3.5 h-3.5 accent-emerald-500 rounded"
              />
              <span>In Stock / Ready to Sell</span>
            </label>
            <button
              type="submit"
              disabled={!newProdName.trim()}
              className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs rounded-xl transition cursor-pointer shadow-lg disabled:opacity-50"
            >
              Add Item
            </button>
          </div>
        </form>
      )}

      {/* ── Edit Product Inline Drawer Form ──────────────────────────── */}
      {editingProd && (
        <form onSubmit={handleSaveEdit} className="p-3.5 bg-sky-950/30 border-b border-sky-500/30 space-y-2.5 animate-fade-in">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-mono font-bold text-sky-400 flex items-center gap-1">
              <Edit3 size={11} /> Edit Product & Stock
            </span>
            <button
              type="button"
              onClick={() => setEditingProd(null)}
              className="text-slate-400 hover:text-slate-200 text-xs"
            >
              Cancel
            </button>
          </div>

          <input
            type="text"
            required
            value={editProdName}
            onChange={(e) => setEditProdName(e.target.value)}
            placeholder="Product Name"
            className="w-full bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
          />

          {/* Multi-Category Selector (Tag Cloud + Quick Add) (Edit) */}
          <div className="space-y-1.5 p-2 rounded-xl bg-slate-950/80 border border-sky-500/30">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-mono font-bold text-sky-300 uppercase flex items-center gap-1.5">
                <Folder size={11} className="text-sky-400" />
                <span>Assigned Categories (Multiple Allowed)</span>
              </label>
              <span className="text-[9px] font-mono text-slate-400">
                {editProdCategories.length} selected
              </span>
            </div>

            {/* Selected category chips */}
            <div className="flex flex-wrap gap-1.5 min-h-[30px] p-1.5 rounded-lg bg-slate-950 border border-sky-500/20 items-center">
              {editProdCategories.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-sky-500/20 text-sky-300 border border-sky-500/40"
                >
                  <Folder size={9} className="text-sky-400" />
                  <span>{cat}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const remaining = editProdCategories.filter((c) => c !== cat);
                      setEditProdCategories(remaining.length > 0 ? remaining : ['General']);
                      setEditProdCategory(remaining[0] || 'General');
                    }}
                    className="text-sky-400 hover:text-rose-400 ml-1 cursor-pointer font-black text-xs"
                    title="Remove category"
                  >
                    ×
                  </button>
                </span>
              ))}

              {/* Inline Input to add/assign category */}
              <input
                type="text"
                list="catalog-categories-list"
                value={editProdCategoryTagInput}
                onChange={(e) => setEditProdCategoryTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = editProdCategoryTagInput.trim();
                    if (val && !editProdCategories.includes(val)) {
                      setEditProdCategories([...editProdCategories.filter((c) => c !== 'General'), val]);
                      setEditProdCategory(val);
                      setEditProdCategoryTagInput('');
                    }
                  }
                }}
                placeholder="+ Add or type category..."
                className="bg-transparent border-none text-[10px] text-white placeholder-slate-500 focus:outline-none font-mono flex-1 min-w-[120px] px-1"
              />
            </div>

            {/* Quick-add available store categories pill bar */}
            {categories.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap pt-0.5">
                <span className="text-[9px] font-mono text-slate-500 uppercase">Quick Add:</span>
                {categories
                  .filter((c) => !editProdCategories.includes(c))
                  .slice(0, 8)
                  .map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        setEditProdCategories([...editProdCategories.filter((c) => c !== 'General'), cat]);
                        setEditProdCategory(cat);
                      }}
                      className="text-[9px] font-mono text-slate-300 hover:text-sky-300 bg-slate-900 hover:bg-sky-950/60 px-1.5 py-0.5 rounded border border-sky-500/20 hover:border-sky-500/40 transition cursor-pointer"
                    >
                      + {cat}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2">
            <input
              type="number"
              step="any"
              required
              value={editProdPrice}
              onChange={(e) => setEditProdPrice(e.target.value)}
              placeholder={`Price (${defaultCurrency}) *`}
              className="bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
            />
            <input
              type="number"
              step="any"
              value={editProdOriginalPrice}
              onChange={(e) => setEditProdOriginalPrice(e.target.value)}
              placeholder="Original Price"
              className="bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2 py-1 text-xs text-slate-400 placeholder-slate-600 focus:outline-none font-mono"
            />
            <input
              type="number"
              value={editProdStockQty}
              onChange={(e) => setEditProdStockQty(e.target.value)}
              placeholder="Stock Units"
              className="bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2 py-1 text-xs text-sky-300 placeholder-slate-600 focus:outline-none font-mono"
            />
            <input
              type="text"
              value={editProdUnit}
              onChange={(e) => setEditProdUnit(e.target.value)}
              placeholder="Unit"
              className="bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none"
            />
          </div>

          <textarea
            rows={2}
            value={editProdDesc}
            onChange={(e) => setEditProdDesc(e.target.value)}
            placeholder="Description & specs"
            className="w-full bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2.5 py-1 text-xs text-white placeholder-slate-500 focus:outline-none resize-none"
          />

          {/* ── Multi-Media, Document Upload & Luna AI Ingestion (Edit) ─ */}
          <div className="p-2.5 rounded-2xl bg-slate-950/70 border border-sky-500/30 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-mono font-bold text-sky-300 flex items-center gap-1">
                <Paperclip size={11} /> Multi-Media & Documents ({editProdMedia.length})
              </span>
              <button
                type="button"
                onClick={() => handleAiImproveProduct(true)}
                disabled={isEditAiEnriching}
                className="px-2.5 py-1 rounded-xl bg-gradient-to-r from-sky-500/30 to-indigo-500/30 hover:from-sky-500/40 hover:to-indigo-500/40 border border-sky-400/60 text-sky-200 hover:text-white font-bold text-[10px] flex items-center gap-1.5 transition shadow-[0_0_15px_rgba(56,189,248,0.3)] disabled:opacity-50 cursor-pointer"
                title="Luna AI analyzes uploaded images, videos & documents to auto-enrich description, category, and keywords"
              >
                {isEditAiEnriching ? (
                  <>
                    <Loader2 size={11} className="animate-spin text-sky-400" />
                    <span>Luna Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Wand2 size={11} className="text-sky-300 animate-pulse" />
                    <span>✨ Improve with Luna AI</span>
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={editProdMediaUrl}
                onChange={(e) => setEditProdMediaUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editProdMediaUrl.trim()) {
                    e.preventDefault();
                    const url = editProdMediaUrl.trim();
                    const ext = url.split('.').pop()?.toLowerCase() || '';
                    let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
                    if (['mp4', 'mov', 'webm'].includes(ext)) mType = 'video';
                    else if (['pdf'].includes(ext)) mType = 'pdf';
                    else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';
                    setEditProdMedia(prev => [...prev, {
                      id: `med_${Date.now()}`,
                      type: mType,
                      url,
                      name: `Asset ${prev.length + 1}`
                    }]);
                    setEditProdMediaUrl('');
                  }
                }}
                placeholder="Paste URL + Enter, or select multiple files →"
                className="flex-1 bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
              />
              <input
                type="file"
                ref={editFileInputRef}
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleMultiFileUpload(e.target.files, true);
                  }
                }}
                accept="image/*,video/*,application/pdf,.doc,.docx,.txt"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => editFileInputRef.current?.click()}
                disabled={isEditUploadingMedia}
                className="px-2.5 py-1 bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 rounded-xl text-[10px] font-bold font-mono flex items-center gap-1 transition cursor-pointer shrink-0 disabled:opacity-50 shadow-sm"
                title="Upload photos, videos, or spec sheets (multiple supported)"
              >
                {isEditUploadingMedia ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Upload size={11} />
                )}
                <span>+ Upload</span>
              </button>
            </div>

            {/* Attached Media Chips */}
            {editProdMedia.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1 max-h-24 overflow-y-auto pr-1">
                {editProdMedia.map((m, idx) => (
                  <div
                    key={m.id || idx}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-sky-950/60 border border-sky-500/40 text-[10px] font-mono text-sky-200"
                  >
                    {m.type === 'image' && (
                      <img
                        src={m.url}
                        alt=""
                        className="w-4 h-4 rounded object-cover border border-sky-400/40"
                        onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                      />
                    )}
                    {m.type === 'video' && <Film size={11} className="text-purple-400" />}
                    {m.type === 'pdf' && <FileText size={11} className="text-rose-400" />}
                    {m.type === 'document' && <FileText size={11} className="text-amber-400" />}
                    <span className="truncate max-w-[100px]" title={m.name || m.url}>
                      {m.name || m.type.toUpperCase()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedPreviewMedia({ title: m.name || 'Preview', url: m.url, type: m.type })}
                      className="text-slate-400 hover:text-sky-300 ml-0.5"
                      title="Preview Media"
                    >
                      <Eye size={10} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditProdMedia(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-rose-400 ml-0.5"
                      title="Remove"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Optional Raw Text / ChatGPT Paste (Edit) */}
            <div className="pt-1.5 border-t border-sky-500/20">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                  <FileText size={10} className="text-sky-400" />
                  <span>Paste ChatGPT / Raw Specs (Optional)</span>
                </span>
                {editProdRawText && (
                  <button
                    type="button"
                    onClick={() => setEditProdRawText('')}
                    className="text-[9px] text-slate-500 hover:text-rose-400 cursor-pointer transition"
                  >
                    Clear
                  </button>
                )}
              </div>
              <textarea
                rows={2}
                value={editProdRawText}
                onChange={(e) => setEditProdRawText(e.target.value)}
                placeholder="Paste ChatGPT output, full descriptions, raw supplier specs, or price lists here... Luna will parse everything!"
                className="w-full bg-slate-900/90 border border-sky-500/20 focus:border-sky-400/70 rounded-xl px-2.5 py-1 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none resize-none font-mono"
              />
            </div>
          </div>

          <input
            type="text"
            value={editProdKeywords}
            onChange={(e) => setEditProdKeywords(e.target.value)}
            placeholder="Keywords (comma separated, auto-enriched by Luna AI)"
            className="w-full bg-slate-950 border border-slate-700 focus:border-sky-400 rounded-xl px-2.5 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
          />

          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 font-mono cursor-pointer">
              <input
                type="checkbox"
                checked={editProdAvailable}
                onChange={(e) => setEditProdAvailable(e.target.checked)}
                className="w-3.5 h-3.5 accent-sky-500 rounded"
              />
              <span>In Stock / Ready to Sell</span>
            </label>
            <button
              type="submit"
              disabled={!editProdName.trim()}
              className="px-3 py-1 bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs rounded-xl transition cursor-pointer shadow-lg disabled:opacity-50"
            >
              Update Product
            </button>
          </div>
        </form>
      )}

      {/* ── Recycle Bin Drawer ────────────────────────────────────────── */}
      {showRecycleBin && (
        <div className="p-3.5 bg-rose-950/25 border-b border-rose-500/30 space-y-2.5 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Trash2 size={13} className="text-rose-400" />
              <span className="text-[11px] font-black font-mono text-rose-300 uppercase tracking-wider">
                Recycle Bin ({rawRecycleBin.length})
              </span>
              <span className="text-[9px] text-slate-400 hidden sm:inline font-mono">
                Auto-safeguard against accidental deletions
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {rawRecycleBin.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleRestoreAllRecycle}
                    className="px-2 py-0.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[9px] font-mono font-bold transition cursor-pointer flex items-center gap-1"
                  >
                    <RotateCcw size={10} />
                    <span>Restore All</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Permanently empty all items from recycle bin?')) {
                        handleEmptyRecycleBin();
                      }
                    }}
                    className="px-2 py-0.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-[9px] font-mono font-bold transition cursor-pointer"
                  >
                    Empty Bin
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setShowRecycleBin(false)}
                className="text-slate-400 hover:text-slate-200 text-xs p-1"
              >
                ✕
              </button>
            </div>
          </div>

          {rawRecycleBin.length === 0 ? (
            <div className="py-6 text-center text-slate-500 text-xs font-mono">
              <Archive size={20} className="mx-auto mb-1.5 text-slate-600" />
              <span>Recycle Bin is empty. No deleted products.</span>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-rose-950 pr-1">
              {rawRecycleBin.map((item) => {
                const p = item.product || (item as any);
                const thumb = p.imageUrl || (p.media && p.media[0]?.url);
                return (
                  <div
                    key={item.id}
                    className="p-2 rounded-xl bg-slate-950/80 border border-rose-500/20 flex items-center justify-between gap-2.5 hover:border-rose-500/40 transition"
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      {thumb ? (
                        <img src={thumb} alt={p.name} className="w-8 h-8 rounded-lg object-cover border border-slate-800 shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 shrink-0">
                          <Package size={14} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-white truncate">{p.name}</div>
                        <div className="text-[9px] text-slate-400 font-mono flex items-center gap-2">
                          <span className="text-emerald-400">{p.currency || defaultCurrency} {Number(p.price || 0).toLocaleString()}</span>
                          <span>•</span>
                          <span>{getProductCategories(p).join(', ')}</span>
                          {item.deletedBy && (
                            <>
                              <span>•</span>
                              <span className={item.deletedBy === 'luna_ai' ? 'text-cyan-400 font-bold' : 'text-slate-400'}>
                                {item.deletedBy === 'luna_ai' ? '🤖 Luna AI' : '👤 Manual'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleRestoreFromRecycle(item.id)}
                        className="px-2.5 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer active:scale-95"
                        title="Restore product back to catalog"
                      >
                        <RotateCcw size={11} />
                        <span>Restore</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePermanentDeleteRecycle(item.id)}
                        className="p-1 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition cursor-pointer"
                        title="Permanently remove"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Snapshots & Backup Drawer ─────────────────────────────────── */}
      {showSnapshots && (
        <div className="p-3.5 bg-indigo-950/25 border-b border-indigo-500/30 space-y-2.5 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <History size={13} className="text-indigo-400" />
              <span className="text-[11px] font-black font-mono text-indigo-300 uppercase tracking-wider">
                Catalog Snapshots & Backups ({rawSnapshots.length})
              </span>
              <span className="text-[9px] text-slate-400 hidden sm:inline font-mono">
                Full checkpoints with 1-click rollback
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setIsCreatingSnapshot(!isCreatingSnapshot)}
                className="px-2 py-0.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/40 text-[9px] font-mono font-bold transition cursor-pointer flex items-center gap-1"
              >
                <Camera size={10} />
                <span>Take Snapshot</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSnapshots(false);
                  setIsCreatingSnapshot(false);
                }}
                className="text-slate-400 hover:text-slate-200 text-xs p-1"
              >
                ✕
              </button>
            </div>
          </div>

          {isCreatingSnapshot && (
            <form onSubmit={handleTakeManualSnapshot} className="p-2.5 rounded-xl bg-slate-950/90 border border-indigo-500/40 flex items-center gap-2 animate-fade-in">
              <input
                type="text"
                value={manualSnapshotLabel}
                onChange={(e) => setManualSnapshotLabel(e.target.value)}
                placeholder="Snapshot Label (e.g. Before Promo, Pre-Price Update...)"
                className="flex-1 bg-slate-900 border border-slate-700 focus:border-indigo-400 rounded-lg px-2.5 py-1 text-xs text-white placeholder-slate-500 focus:outline-none"
              />
              <button
                type="submit"
                className="px-3 py-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-bold text-xs font-mono transition cursor-pointer shadow-md shadow-indigo-500/20"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setIsCreatingSnapshot(false)}
                className="text-slate-400 hover:text-slate-200 text-xs"
              >
                Cancel
              </button>
            </form>
          )}

          {rawSnapshots.length === 0 ? (
            <div className="py-6 text-center text-slate-500 text-xs font-mono">
              <History size={20} className="mx-auto mb-1.5 text-slate-600" />
              <span>No snapshots recorded yet. Auto-snapshots are saved before deletions.</span>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-indigo-950 pr-1">
              {rawSnapshots.map((snap) => (
                <div
                  key={snap.id}
                  className="p-2 rounded-xl bg-slate-950/80 border border-indigo-500/20 flex items-center justify-between gap-2.5 hover:border-indigo-500/40 transition"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white truncate">{snap.label}</span>
                      <span className={`text-[8px] font-mono px-1 py-0.2 rounded border ${
                        snap.trigger === 'pre_delete'
                          ? 'bg-rose-500/20 text-rose-300 border-rose-500/30'
                          : snap.trigger === 'restore'
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                            : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                      }`}>
                        {snap.trigger?.toUpperCase() || 'MANUAL'}
                      </span>
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono flex items-center gap-2 mt-0.5">
                      <span className="text-indigo-300 font-bold">📦 {snap.productCount || snap.products?.length || 0} Items</span>
                      <span>•</span>
                      <span>{new Date(snap.timestamp).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Rollback entire catalog to snapshot "${snap.label}" (${snap.productCount} products)? Current state will be auto-saved first.`)) {
                          handleRestoreSnapshot(snap.id);
                        }
                      }}
                      className="px-2.5 py-1 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-500/40 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer active:scale-95"
                      title="Rollback entire product catalog to this snapshot"
                    >
                      <RotateCcw size={11} />
                      <span>Rollback</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSnapshot(snap.id)}
                      className="p-1 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition cursor-pointer"
                      title="Delete snapshot"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Product List Feed ────────────────────────────────────────── */}
      <div className="p-3 max-h-[340px] overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-slate-800">
        {/* Active Category Header & Rules / Description Banner */}
        {selectedCategory !== 'all' && (
          <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-[#0C241B] via-[#091B13] to-[#071610] p-3 shadow-md space-y-2 relative overflow-hidden group mb-2">
            <div className="flex flex-wrap items-center justify-between gap-2 relative z-10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">
                  <Folder size={15} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs font-black text-white font-mono">
                      {selectedCategory}
                    </h3>
                    <span className="text-[9px] font-mono text-emerald-300 bg-emerald-950/80 px-1.5 py-0.2 rounded-full border border-emerald-500/40 font-bold">
                      {products.filter(p => getProductCategories(p).some(c => c.toLowerCase() === selectedCategory.toLowerCase())).length} Items
                    </span>
                  </div>
                  <p className="text-[9px] text-slate-400 font-sans">
                    Luna AI quotes these category rules & rates to customers
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    const currentRule = categoryRules[selectedCategory] || '';
                    setEditingCategoryRule({ catName: selectedCategory, rule: currentRule });
                  }}
                  className="text-[10px] font-mono font-bold text-cyan-300 hover:text-white bg-cyan-950/60 hover:bg-cyan-900/80 px-2 py-1 rounded-lg border border-cyan-500/40 hover:border-cyan-400 transition flex items-center gap-1 shadow-sm cursor-pointer"
                >
                  <Edit2 size={11} className="text-cyan-400" />
                  <span>{categoryRules[selectedCategory] ? 'Edit Rules' : '+ Add Rules'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setNewProdCategories([selectedCategory]);
                    setNewProdCategory(selectedCategory);
                    setIsAddingProduct(true);
                  }}
                  className="text-[10px] font-mono font-bold text-slate-950 bg-emerald-400 hover:bg-emerald-300 px-2 py-1 rounded-lg transition flex items-center gap-1 shadow-sm cursor-pointer"
                >
                  <Plus size={11} />
                  <span>Add Item</span>
                </button>
              </div>
            </div>

            {/* Description / Rules Display or Inline Editor */}
            {editingCategoryRule?.catName === selectedCategory ? (
              <div className="mt-1.5 p-2 bg-[#06120D] border border-cyan-500/40 rounded-xl space-y-1.5 relative z-10 animate-fade-in">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-mono font-bold text-cyan-300 flex items-center gap-1">
                    <FileText size={11} className="text-cyan-400" />
                    <span>Edit Rules & Rates for &quot;{selectedCategory}&quot;:</span>
                  </label>
                </div>
                <textarea
                  rows={2}
                  value={editingCategoryRule.rule}
                  onChange={(e) => setEditingCategoryRule({ ...editingCategoryRule, rule: e.target.value })}
                  placeholder="e.g. 100km: Rs. 5,000 | Extra km: Rs. 40 | Deposit: Rs. 15,000"
                  className="w-full bg-[#030906] border border-cyan-500/30 focus:border-cyan-400 rounded-lg p-1.5 text-[11px] text-white placeholder-slate-500 font-sans focus:outline-none"
                />
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => setEditingCategoryRule(null)}
                    className="px-2 py-0.5 rounded text-[10px] font-mono text-slate-400 hover:text-white bg-slate-900 border border-slate-700 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveCategoryRule(editingCategoryRule.catName, editingCategoryRule.rule)}
                    className="px-2.5 py-0.5 rounded text-[10px] font-mono font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 transition cursor-pointer flex items-center gap-1 shadow-sm"
                  >
                    <Check size={11} />
                    <span>Save Rules</span>
                  </button>
                </div>
              </div>
            ) : categoryRules[selectedCategory] ? (
              <div className="mt-1 p-2 bg-[#06120D]/90 border border-emerald-500/20 rounded-lg flex items-start gap-2 relative z-10">
                <FileText size={13} className="text-cyan-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] font-mono font-bold text-cyan-300 uppercase tracking-wider">
                    Rules & Policy:
                  </div>
                  <p className="text-[11px] text-cyan-100 font-sans leading-tight whitespace-pre-wrap mt-0.5">
                    {categoryRules[selectedCategory]}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-1 p-1.5 bg-[#06120D]/60 border border-dashed border-emerald-500/30 rounded-lg flex items-center justify-between gap-1.5 relative z-10">
                <div className="flex items-center gap-1 text-[10px] text-slate-400">
                  <AlertCircle size={12} className="text-amber-400 shrink-0" />
                  <span>No rules defined for <strong>{selectedCategory}</strong> yet.</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategoryRule({ catName: selectedCategory, rule: '' });
                  }}
                  className="text-[10px] font-mono text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer"
                >
                  + Add Rules
                </button>
              </div>
            )}
          </div>
        )}

        {filteredProducts.length === 0 ? (
          <div className="p-6 text-center bg-slate-900/40 rounded-2xl border border-slate-800/80">
            <Package size={28} className="mx-auto text-slate-600 mb-2 opacity-60" />
            <p className="text-xs text-slate-400 font-medium">No catalog items found.</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Click &quot;+&quot; to add your first product or dish.</p>
          </div>
        ) : viewMode === 'grouped' ? (
          <div className="space-y-3">
            {categories
              .filter(c => selectedCategory === 'all' || selectedCategory.toLowerCase() === c.toLowerCase())
              .map(cat => {
                const catProds = filteredProducts.filter(p => getProductCategories(p).some(c => c.toLowerCase() === cat.toLowerCase()));
                if (catProds.length === 0 && selectedCategory !== 'all') return null;
                return (
                  <div key={cat} className="rounded-2xl border border-slate-800/80 bg-slate-950/60 overflow-hidden shadow-sm">
                    <div className="px-3 py-1.5 bg-slate-900/80 border-b border-slate-800/80 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-emerald-400 font-mono flex items-center gap-1.5">
                          📁 {cat}
                        </span>
                        <span className="text-[9px] font-mono text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded">
                          {catProds.length} {catProds.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setNewProdCategory(cat);
                          setNewProdCategories([cat]);
                          setSelectedCategory(cat);
                          setIsAddingProduct(true);
                        }}
                        className="text-[10px] font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 bg-emerald-950/50 hover:bg-emerald-900/50 px-2 py-0.5 rounded-lg border border-emerald-500/30 transition cursor-pointer"
                      >
                        <Plus size={11} />
                        <span>Add to {cat}</span>
                      </button>
                    </div>
                    {categoryRules[cat] && (
                      <div className="px-3 py-1 bg-[#06120D] border-b border-emerald-500/20 text-[10px] text-cyan-300 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <FileText size={11} className="text-cyan-400 shrink-0" />
                          <span className="text-slate-400 font-medium">Rules & Rates:</span>
                          <span className="text-cyan-200 truncate">{categoryRules[cat]}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCategoryManager(true);
                            setEditingCategoryRule({ catName: cat, rule: categoryRules[cat] || '' });
                          }}
                          className="text-[9px] font-bold text-cyan-400 hover:underline shrink-0"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                    <div className="p-2 space-y-2">
                      {catProds.length === 0 ? (
                        <div className="text-center py-2 text-[10px] text-slate-500 italic">
                          No items in this category yet. Click &quot;Add to {cat}&quot; above!
                        </div>
                      ) : (
                        catProds.map(renderProductCard)
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          filteredProducts.map(renderProductCard)
        )}
      </div>
      </>
      )}

      {/* ── Footer & Navigation ─────────────────────────────────────── */}
      <div className="p-3 border-t border-slate-800/80 bg-slate-900/40 rounded-b-3xl flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono">
          <Zap size={12} className="text-emerald-400" />
          <span>Synchronized with Luna AI Brain</span>
        </div>

        {openPortalData && (
          <button
            type="button"
            onClick={() => openPortalData('product_catalog')}
            className="text-[10px] font-bold text-emerald-300 hover:text-white bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 px-2 py-1 rounded-lg flex items-center gap-1 transition cursor-pointer"
          >
            <span>Open Studio</span>
            <ExternalLink size={11} />
          </button>
        )}
      </div>

      {/* ── Right Source Handle (Downstream to Luna Core) ─────────────── */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-catalog"
        className="!bg-emerald-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-20 shadow-[0_0_12px_rgba(16,185,129,0.8)]"
        title="Output: Product Catalog Context to Luna Core Hub"
      />

      {/* ── Media Lightbox Preview Modal ─────────────────────────────── */}
      {selectedPreviewMedia && (
        <div
          className="fixed inset-0 z-[9999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 nodrag cursor-default"
          onClick={() => setSelectedPreviewMedia(null)}
        >
          <div
            className="relative max-w-lg w-full bg-slate-900 border border-emerald-500/40 rounded-3xl p-4 shadow-2xl space-y-3 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800/80 pb-2">
              <span className="text-xs font-bold text-emerald-300 font-mono truncate max-w-[300px]">
                {selectedPreviewMedia.title}
              </span>
              <button
                type="button"
                onClick={() => setSelectedPreviewMedia(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              >
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-auto flex items-center justify-center bg-black/60 rounded-2xl p-2 border border-slate-800">
              {selectedPreviewMedia.type === 'image' && (
                <img
                  src={selectedPreviewMedia.url}
                  alt={selectedPreviewMedia.title}
                  className="max-h-[50vh] max-w-full rounded-xl object-contain shadow-lg"
                />
              )}
              {selectedPreviewMedia.type === 'video' && (
                <video
                  src={selectedPreviewMedia.url}
                  controls
                  autoPlay
                  className="max-h-[50vh] max-w-full rounded-xl shadow-lg"
                />
              )}
              {(selectedPreviewMedia.type === 'pdf' || selectedPreviewMedia.type === 'document') && (
                <div className="p-6 text-center space-y-3">
                  <FileText size={42} className="mx-auto text-emerald-400 animate-pulse" />
                  <p className="text-xs text-slate-300 font-medium">{selectedPreviewMedia.title}</p>
                  <a
                    href={selectedPreviewMedia.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs shadow-lg transition"
                  >
                    <span>Open in New Tab</span>
                    <ExternalLink size={13} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ProductCatalogNode.displayName = 'ProductCatalogNode';
